import React, { useState, useRef, useEffect, useLayoutEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import { nanoid } from 'nanoid';

import {
    getDiagramDoc,
    useElements,
    useLine,
    useLines,
    useArea,
    useRole,
} from '../../lib/yjs';

import {
    deleteElements,
    setElementDescription,
    setNoteAlignment,
    setNoteFontMonospace,
    setNoteText,
    duplicateElements,
    duplicateGroup,
} from '../../lib/yjs/mutators/elements';

import { toggleStyleAll, toggleListAll, isStyleOnAll, isListAll } from '../../lib/noteHtml/htmlNoteOps';
import { normalizeLinkUrl } from '../../lib/noteHtml/linkSchemes';
import { getNoteEditor } from '../EditableElements/EditableNote/noteEditorBridge';
import { subscribeNoteLinkEditRequest } from '../EditableElements/EditableNote/noteLinkEditRequestBridge';

import {
    addColumn,
    deleteColumn,
    moveColumnAbove,
    moveColumnBelow,
    duplicateColumn,
    setColumnDescription,
    setColumnKey,
    setColumnIndex,
    setColumnNotNull,
    setColumnAutoIncrement,
    setColumnEnum,
    setColumnDefaultValue,
    setColumnGeneratedValue,
    setColumnCheckValue,
} from '../../lib/yjs/mutators/columns';

import {
    addLine,
    deleteLine,
    setLineColor,
    setLineConnector,
    setLineStrokeStyle,
    setLineConnectorsReversed,
    setLineOnTrigger,
    setLineDescription,
} from '../../lib/yjs/mutators/lines';

import {
    addAreaForFocusedElements,
    deleteArea,
    duplicateArea,
    setAreaColor,
    setAreaBorder,
    setAreaDescription,
} from '../../lib/yjs/mutators/areas';

import {
    setElementColor,
    setElementBorder,
} from '../../lib/yjs/mutators/elements';

// eslint-disable-next-line
import {
    toggleVisibilityContextMenu,
    showContextMenu,
    // hideContextMenu,
    // autoResizeElements,
    toggleBtnNewLine,
    // clickEscape,
    enableElementDescriptionEditing,
    disableElementDescriptionEditing,
    updateFocusedInsideElement,
    updateFocusedIds,
    enableCanvasPricingPlanLimit,
} from '../../redux/diagram/diagram.actions';
import { showNotify } from '../../redux/notify/notify.actions';
import { usePlanGate } from '../../hooks/usePlanGate';

// eslint-disable-next-line
import {
    selectFocusedInsideElement,
    selectIsContextMenuShowed,
    selectIsContextMenuVisible,
    selectFocusedIds,
    selectIsBtnNewLineEnabled,
    selectViewMode,
    selectIsBtnPopupNewsEnabled,
    selectIsDiagramReadOnly,
    selectIsBtnSearchEnabled,
} from '../../redux/diagram/diagram.selectors';

import { selectResolvedTheme } from '../../redux/preferences/preferences.selectors';

import {
    selectIsModalSupportEnabled,
    selectIsModalDiagramDetailsEnabled,
    selectIsModalDiagramShareEnabled,
} from '../../redux/main/main.selectors';

// eslint-disable-next-line
import {
    TableGroupNewSmallIcon,
    ElementDescriptionIcon,
    DragMenuIcon,
    VisibilityNewIcon,
    // VisibilityIcon,
    DraggableColumnsIcon,
    DraggableColumnsReverseIcon,
    ShowMenuIcon,
    // HideMenuIcon,
    MarkYesIcon,
    MarkNoIcon,
    CircleNoneIcon,
    KeyIcon,
    CommentIconNew,
    // DragColumnIcon,
    // LineIcon,
    LineIconNewSmall,
    // FitToZoomIcon,
    // AutoLayoutIcon,
    CopyIcon,
    InsertAfterIcon,
    InsertBeforeIcon,
    DeleteIcon,
    // TableDarkIcon,
    // TableLightIcon
    // SwapLinesIcon,
    SwapLinesIconNew,
    LineConnectorEmptyIcon,
    LineConnectorOneIcon,
    LineConnectorManyIcon,
    LineConnectorOneNullIcon,
    LineConnectorManyNullIcon,
    LineTypeIcon,
    ElementBorderIcon,
    // LineTypeDashesIcon,
    BackgroundColorCircleIcon,
    FontMonospaceIcon,
    NoteSmallIcon,
    AlignLeftIcon,
    AlignRightIcon,
    AlignCenterIcon,
    AlignLeftIconNew,
    AlignRightIconNew,
    AlignCenterIconNew,
    AlignTopIcon,
    AlignBottomIcon,
    AlignVerticalCenterIcon,
    LinkIcon,
    BoldTextIcon,
    ItalicTextIcon,
    UnderlineTextIcon,
    StrikethroughTextIcon,
    TagIcon,
    OkCheckIcon,
    TriggerIcon,
    GeneratedColumnIcon,
    CheckExpressionIcon,
} from '../Icon';

import { useShortKeysNavigation } from '../../hooks/useShortKeysNavigation';

import './ContextMenu.scss';

function getLineConnector(relationType, isLeft = false) {
    switch (relationType) {
        case 'empty': {
            return <LineConnectorEmptyIcon />;
        }
        case 'one': {
            return <LineConnectorOneIcon isLeft={isLeft} />;
        }
        case 'many': {
            return <LineConnectorManyIcon isLeft={isLeft} />;
        }
        case 'one-null': {
            return <LineConnectorOneNullIcon isLeft={isLeft} />;
        }
        case 'many-null': {
            return <LineConnectorManyNullIcon isLeft={isLeft} />;
        }
        default:
            return <LineConnectorEmptyIcon />;
    }
}

const FILTER_TYPE_LABELS = {
    table: ['Table', 'Tables'],
    note: ['Note', 'Notes'],
    'pen-segment': ['Pen', 'Pens'],
    area: ['Area', 'Areas'],
};

// Fixed order the filter sub-menu lists types in: tables, notes, areas, then pens.
const FILTER_TYPE_ORDER = ['table', 'note', 'area', 'pen-segment'];

// Group a selection ({ id: element }) by element type → { type: { count, ids } }.
function filterSelectionCounts(focusedElements) {
    const counts = {};
    for (const id in focusedElements) {
        const type = focusedElements[id]?.type || 'element';
        if (!counts[type]) counts[type] = { count: 0, ids: [] };
        counts[type].count++;
        counts[type].ids.push(id);
    }
    return counts;
}

function filterTypeLabel(type, count) {
    const [singular, plural] = FILTER_TYPE_LABELS[type] || ['Element', 'Elements'];
    return `${count} ${count === 1 ? singular : plural}`;
}

function filterSelectionType(type, count) {
    const [singular, plural] = FILTER_TYPE_LABELS[type] || ['Element', 'Elements'];
    // return `${count === 1 ? singular : plural}`;
    return `${plural}`;
}

// "5 Tables" when the selection is a single type, "8 Elements" when it is mixed.
function filterSelectionLabel(focusedElements) {
    const total = Object.keys(focusedElements).length;
    const types = Object.keys(filterSelectionCounts(focusedElements));
    if (types.length === 1) return filterTypeLabel(types[0], total);
    return `${total} Element${total === 1 ? '' : 's'}`;
}

// note link submenu — requires the note's editor overlay to be open with an
// active (non-collapsed) text selection AT THE MOMENT THE LINK BUTTON IS
// CLICKED. Unlike every other submenu here, NoteLinkMenu holds NO state of
// its own (not even the captured selection/anchor) and is NOT positioned in
// the toolbar's own menu column — see its own doc comment below for both.
// Everything it needs is captured by the Link button's onClick handler
// (search this file for `noteLinkMenuArgsRef`) BEFORE the submenu opens, and
// handed down as plain props: beginLinkTarget()+getSelectionStyles() (for
// canLink + the URL pre-fill) and getLinkTargetRect() (for the popover's
// anchor point, the bounding rect of a cloned Range — see EditableNote.jsx's
// own doc comment on the DOM highlight LAYER this now drives: plain
// absolutely-positioned <div>s painted between the note's background and
// its text, not the marker-span DOM mutation or CSS Custom Highlight API
// this has gone through in earlier iterations). getSelectionSnapshot/
// applyLinkAt/removeLinkAt still exist on the bridge (EditableNote.jsx) for
// any other caller — this menu doesn't use them.
//
// Content-fit sizing for NoteLinkMenu's URL input (below) — module scope,
// same reasoning as truncateLink in NoteLinkPreview.jsx: stateless helpers,
// no reason to live inside the component or be re-created every render.
//
// A single shared offscreen <canvas> 2D context, created lazily on first
// use and reused for every measurement afterward (creating a fresh canvas
// per keystroke would be wasteful, and unnecessary — measureText doesn't
// touch anything about a PREVIOUS measurement's result). `undefined` (not
// yet attempted) is distinguished from `null` (attempted, unavailable) so
// this only ever tries once, not once per call.
let noteLinkMeasureCtx;
const getNoteLinkMeasureCtx = () => {
    if (noteLinkMeasureCtx !== undefined) return noteLinkMeasureCtx;
    try {
        noteLinkMeasureCtx = document.createElement('canvas').getContext('2d') || null;
    } catch (e) {
        noteLinkMeasureCtx = null;
    }
    return noteLinkMeasureCtx;
};

// ~0.55x font-size is a rough average glyph-width ratio for proportional UI
// fonts (Inter/Roboto, this input's own font-family) — ONLY used as a
// fallback when a canvas 2D context isn't available at all (getContext('2d')
// can return null — e.g. this repo's own jsdom test environment, which
// doesn't implement canvas rendering; or a locked-down browser). The
// primary path below is canvas measureText, not this.
const NOTE_LINK_FALLBACK_CHAR_WIDTH_RATIO = 0.55;

// Small fixed allowance beyond the raw glyph measurement — room for the
// text caret itself plus sub-pixel rounding, so the last character typed
// doesn't end up sitting flush against the input's own padding/edge.
const NOTE_LINK_CARET_ALLOWANCE_PX = 4;

// Measures `text` against the INPUT'S OWN actual computed font (family,
// size, weight) via canvas measureText — pixel-accurate against whatever
// ContextMenu.scss's `.enum-input.link-input` cascade actually renders,
// rather than this file hardcoding a guess at that font that would
// silently drift out of sync with the stylesheet the next time someone
// edits it (this area is heavily user-styled — see the input's own doc
// comment below). Falls back to the flat ch-style estimate above only when
// canvas measurement itself isn't available.
const measureNoteLinkTextWidth = (text, computedStyle) => {
    const ctx = getNoteLinkMeasureCtx();
    if (ctx) {
        ctx.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
        return ctx.measureText(text).width;
    }
    const fontSizePx = parseFloat(computedStyle.fontSize) || 14;
    return text.length * fontSizePx * NOTE_LINK_FALLBACK_CHAR_WIDTH_RATIO;
};

// Sets the input's inline width to fit `text` — used both at open (fits
// prefillHref) and on every keystroke (fits the live value), same rule
// either way. Reads padding/border/letter-spacing off the input's OWN
// computed style (not hardcoded) so this stays correct if that cascade
// changes independently of this file.
//
// Deliberately does NOT reimplement a floor/ceiling clamp here: `.link-
// input`'s own `min-width: 210px` / `max-width: 500px` (ContextMenu.scss)
// already clamp the browser's RENDERED ("used") width regardless of what
// pixel value this sets on the inline style — that's the CSS box model's
// own min/max-width behavior, unconditional on where `width` came from.
// Setting a value outside that range here is therefore harmless (the
// visible result is clamped either way) — and leaves the actual clamp
// numbers owned by the stylesheet alone (one source of truth for the
// "current default width" floor and the "keeps the popover inside the
// viewport" ceiling), instead of duplicating/hardcoding them a second time
// in JS where they could silently drift out of sync with a future SCSS
// edit.
const resizeNoteLinkInputToContent = (el, text) => {
    if (!el) return;
    const value = text ?? '';
    const style = window.getComputedStyle(el);
    const horizontalChrome =
        (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) +
        (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
    const letterSpacingPx = parseFloat(style.letterSpacing) || 0; // "normal" -> NaN -> 0
    const extraLetterSpacing = value.length > 1 ? (value.length - 1) * letterSpacingPx : 0;
    const textWidth = measureNoteLinkTextWidth(value, style) + extraLetterSpacing;
    const nextWidth = Math.max(0, Math.ceil(textWidth + horizontalChrome + NOTE_LINK_CARET_ALLOWANCE_PX));
    el.style.width = `${nextWidth}px`;
};

// Hoisted to MODULE scope (not a nested component defined inside
// ContextMenu's render): a function component defined inside another
// component's render body gets a brand-new identity every render, so React
// would unmount/remount it whenever the parent re-renders while it's on
// screen. No longer strictly load-bearing now that this component has no
// state of its own to lose on remount, but still avoids the churn.
const NoteLinkMenu = ({ editor, canLink, prefillHref, anchorRect, canEdit, t, onSubMenuClose, popoverRef }) => {
    // Uncontrolled input — no useState anywhere in this component (see file-
    // top note): Apply reads the value straight off the DOM via this ref
    // instead of mirroring it into React state on every keystroke.
    const inputRef = useRef(null);

    // useDispatch, not useState — a stable reference to the store's own
    // dispatch function, same as ContextMenu's own top-level `dispatch`
    // just called locally here instead of threaded down as a prop. Only
    // used for the unsupported-protocol notify below.
    const dispatch = useDispatch();

    // Owns the highlight paint's on-screen lifecycle, symmetrically: mount
    // repaints it (repaintLinkTarget, from the capture the Link button's
    // onClick already made via beginLinkTarget — see EditableNote.jsx's own
    // doc comment on why this is a SEPARATE call, not just relying on that
    // earlier paint to still be there), cleanup un-paints it
    // (resolveLinkTarget(null) — un-paint ONLY, the capture itself survives
    // a cleanup, see that function's own doc comment for why).
    //
    // This symmetry (not just a bare cleanup, which is what this used to
    // be) is what makes the whole flow survive React StrictMode (dev only):
    // StrictMode double-invokes a FRESH effect as mount -> cleanup -> mount
    // before settling. Without the mount half repainting, that phantom
    // cleanup would unpaint the highlight (painted by beginLinkTarget just
    // before this component even mounted) and nothing would ever put it
    // back — the highlight would silently never appear in dev, exactly the
    // bug this fixes. With it: paint (button click) -> repaint (mount 1,
    // redundant but harmless) -> unpaint (phantom cleanup, capture
    // survives) -> repaint (mount 2, from the surviving capture) = ends
    // painted. A genuine close (Apply, Remove, outside-click, Escape,
    // switching menus, or the note's own editor closing mid-flow — every
    // one of them unmounts this component, since clickedItem stops being
    // 'note-link') only ever runs the cleanup ONCE, with no second mount to
    // follow it, so it ends un-painted, correctly. Keyed on `editor` (not
    // []) so the OLD editor still gets repainted/cleared if the prop itself
    // changes out from under this component before it unmounts.
    useEffect(() => {
        if (editor) editor.repaintLinkTarget();
        return () => { if (editor) editor.resolveLinkTarget(null); };
    }, [editor]);

    // Sizes the input to fit prefillHref AT OPEN (resizeNoteLinkInputToContent
    // above handles growing/shrinking again on every keystroke — see this
    // input's own onInput below). MUST run — and, being a layout effect,
    // DOES run, synchronously, in declaration order — BEFORE the position/
    // clamp layout effect immediately below: that effect reads the
    // POPOVER's own getBoundingClientRect(), which includes this input's
    // width as one of its children, so the resize has to already be
    // reflected in the DOM by the time that rect is read, or the popover
    // would get positioned/clamped against its OLD (pre-resize) size and
    // only jump to its correct size on some LATER render — exactly the
    // "visible jump" the position effect's own doc comment (right below)
    // already goes out of its way to avoid for its own top/left. React
    // guarantees this ordering on its own: every useLayoutEffect in a
    // single component commit runs synchronously, in the order they were
    // called during render, all before the browser paints — no explicit
    // sequencing needed beyond "declare this one first", which is why it's
    // physically placed above the position effect here instead of after it.
    useLayoutEffect(() => {
        resizeNoteLinkInputToContent(inputRef.current, prefillHref || '');
    }, [prefillHref]);

    // Positions the popover below the selected text (or above it, if there's
    // no room below), clamped to the viewport — computed ONCE, right after
    // mount, not tracked on scroll/resize. `anchorRect` (the bounding rect of
    // the cloned Range captured at open time, viewport-relative — see
    // EditableNote.jsx's getLinkTargetRect) is captured by the caller before
    // this component ever mounts, but this popover's OWN size isn't knowable
    // until it has actually rendered, so the clamping math has to happen
    // here, against the real DOM node, in a layout effect (before paint, so
    // there is no visible jump from the unclamped inline style below to
    // this). Set imperatively on the node itself rather than through React
    // state, same reason as the input above. Runs AFTER the input's own
    // resize effect above (see that effect's own doc comment) so this rect
    // read already reflects the resized input.
    useLayoutEffect(() => {
        const el = popoverRef.current;
        if (!el || !anchorRect) return;
        const MARGIN = 6;
        const { width: popW, height: popH } = el.getBoundingClientRect();
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;

        let top = anchorRect.bottom + MARGIN;
        if (top + popH > viewportH - MARGIN) top = anchorRect.top - popH - MARGIN;
        top = Math.max(MARGIN, top);

        let left = anchorRect.left;
        if (left + popW > viewportW - MARGIN) left = viewportW - MARGIN - popW;
        left = Math.max(MARGIN, left);

        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
    }, [anchorRect, popoverRef]);

    // Both the Apply button's onClick and the input's Enter keydown (below)
    // funnel through this ONE function, so they can never drift apart —
    // normalizeLinkUrl (src/lib/noteHtml/linkSchemes.js) is the single spot
    // that decides what raw input text turns into before it ever reaches
    // editor.resolveLinkTarget (which still runs its own isAllowedLinkUrl
    // write-gate as final defense; this is a courtesy layer in front of it,
    // not a replacement).
    //
    //   - empty (after trim): silent no-op — nothing to apply, nothing to
    //     complain about either (an empty link input is just "didn't fill
    //     it in yet", not a mistake).
    //   - unsupported_protocol (ws:, ftp:, javascript:, ...): notify and
    //     bail WITHOUT closing the popover or touching the input — the
    //     uncontrolled input still has the user's exact text in it (nothing
    //     here ever wrote to it), so they can just edit it and retry.
    //   - success: apply the NORMALIZED url (not the raw input value — a
    //     bare "example.com" becomes "https://example.com", "a@b.com"
    //     becomes "mailto:a@b.com") and close, same as today.
    function handleLinkApply() {
        if (!canEdit || !canLink || !editor) return;
        const raw = inputRef.current ? inputRef.current.value : '';
        const result = normalizeLinkUrl(raw);
        if (result.error === 'empty') return;
        if (result.error === 'unsupported_protocol') {
            dispatch(showNotify({ message: 'note_link_unsupported_protocol' }));
            return;
        }
        editor.resolveLinkTarget(result.url);
        // editor.focus();
        onSubMenuClose();
    }

    function handleLinkRemove() {
        if (!canEdit || !canLink || !editor) return;
        editor.removeLinkTarget();
        // editor.focus();
        onSubMenuClose();
    }

    // Enter applies exactly like the Apply button (same handleLinkApply
    // above, same normalization/validation) — preventDefault unconditionally
    // so the keystroke never does anything else (e.g. a stray native
    // form-submit-like default); stopPropagation too, so it can't also reach
    // the document-level handleKeyPress listener below (ContextMenu's own
    // giant keydown switch) — belt-and-braces, since that listener doesn't
    // currently special-case Enter for clickedItem === 'note-link' anyway
    // (it falls into the isOpenNoteEditing branch, which has no bare-Enter
    // case of its own), but this keeps that true even if it grows one later.
    // Escape is NOT handled here on the input — it's handled once, for the
    // whole popover, by handlePopoverKeyDown below (see that function's own
    // doc comment for why it has to be handled LOCALLY rather than by the
    // document-level listener).
    function handleLinkInputKeyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            handleLinkApply();
        }
    }

    // Grows/shrinks the input on every keystroke — same content-fit rule as
    // the open-time layout effect above (resizeNoteLinkInputToContent is the
    // one place both call), just imperative and per-input instead of a
    // layout effect: onInput already fires synchronously after the DOM
    // value has changed, so there's no React state round-trip to wait on
    // (and, per this file's own top note, NoteLinkMenu has none to round-
    // trip through anyway) — reading event.target.value straight off the
    // native event and writing straight back to event.target.style.width in
    // the same handler is the whole update.
    function handleLinkInputInput(event) {
        resizeNoteLinkInputToContent(event.target, event.target.value);
    }

    // Small focus trap over the popover's own focusables (spec: "input,
    // Apply, Remove when present") — queried fresh off the DOM on every Tab
    // press rather than hard-coded to exactly two refs, so it stays correct
    // if/when the Remove button (currently commented out in the JSX below)
    // gets re-enabled, with zero changes needed here. Deliberately does NOT
    // rely on the browser's own native Tab order (which would require this
    // popover's controls to be the ONLY positive-tabIndex elements on the
    // whole page — the input's tabIndex="2" below is already a legacy
    // artifact of that fragile assumption): every Tab press inside the
    // popover is fully hijacked and focus is moved explicitly, so it can
    // never escape to whatever the rest of the page's tab order happens to
    // be while this is open.
    //
    // Button mousedown-preventDefault and Tab focus are unrelated: a
    // mousedown listener's preventDefault only suppresses the browser's own
    // "focus this element on mousedown" default action for POINTER
    // interaction — it has no effect on keyboard-driven Tab traversal or on
    // an explicit el.focus() call like the ones below (confirmed by reading;
    // this popover's Apply button has no mousedown handler at all today, so
    // there is nothing here that could conflict either way).
    //
    // ALSO handles Escape, in this same local handler — this is not just a
    // style choice. An earlier version of this file assumed Escape reached
    // the note-link popover via ContextMenu's own document-level
    // handleKeyPress listener (search this file for that function): it
    // does not. clickedItem can only be 'note-link' while a note is in
    // text-edit mode (both the toolbar Link button's onClick and the
    // Edit-request bridge require an open note editor to capture a
    // selection/anchor against — see this component's own top-of-file doc
    // comment), which makes isOpenNoteEditing true, which routes
    // handleKeyPress into its `isOpenTextEditing || isOpenNoteEditing`
    // branch — and that branch's own Escape case is commented out (search
    // this file for "NEW Escape"), so nothing there ever runs. Rather than
    // touch that shared, heavily-branched listener (other features live in
    // its other branches), Escape is handled right here instead: same
    // preventDefault+stopPropagation shape as Tab (the underlying
    // contentEditable must not see it either — it's a sibling DOM subtree,
    // not an ancestor, so it wouldn't naturally receive this event anyway,
    // but suppressing it here is the same defensive belt-and-braces as
    // Enter above), then the exact same close path Apply/Remove already use
    // (onSubMenuClose — this component's `t`/`onSubMenuClose` prop is
    // `handleSubMenuClick` at the call site below): sets clickedItem back
    // to null, unmounting this component, whose own cleanup effect
    // (un-paints the highlight, see its own doc comment) already runs on
    // ANY unmount — Escape needed no new cleanup of its own, just a path
    // that actually triggers the existing one.
    function handlePopoverKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onSubMenuClose();
            return;
        }

        if (event.key !== 'Tab') return;
        const container = popoverRef.current;
        if (!container) return;
        const focusables = Array.from(container.querySelectorAll('input, button'))
            .filter((el) => !el.disabled);
        if (!focusables.length) return;

        event.preventDefault();
        event.stopPropagation();

        const currentIndex = focusables.indexOf(document.activeElement);
        const lastIndex = focusables.length - 1;
        let nextIndex;
        if (event.shiftKey) {
            nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
        } else {
            nextIndex = currentIndex === -1 || currentIndex === lastIndex ? 0 : currentIndex + 1;
        }
        focusables[nextIndex].focus();
    }

    // No anchor at all (shouldn't happen — the click handler always supplies
    // one, falling back to the Link button's own rect when there's nothing
    // to mark) means nothing sane to position against; bail rather than
    // render at (0, 0).
    if (!anchorRect) return null;

    // Portaled straight to document.body (not rendered inline in the
    // toolbar's own menu column, unlike every other submenu here): it needs
    // to float below the selected text anywhere on the canvas, which can be
    // arbitrarily far from the toolbar itself, and `position: fixed`
    // coordinates only work cleanly with nothing else deciding this
    // element's containing block. Reuses `.context-menu`'s own dark-menu
    // cascade verbatim by sharing its class name (same nested .menu/
    // .controls-group/.control/.control-button/.link-add/.enum-input rules,
    // zero duplication) — `mouse-hovered` is added unconditionally (rather
    // than driven by real hover tracking, which nothing wires up for a
    // portaled node) so this popover doesn't inherit the toolbar's
    // fade-when-not-hovered behavior and sits fully visible the whole time
    // it's open. `note-link-popover` is a hook for the couple of overrides
    // that behavior needs (EditableNote... see ContextMenu.scss).
    return createPortal(
        <div
            className="context-menu mouse-hovered note-link-popover"
            ref={popoverRef}
            style={{ position: 'fixed', top: `${anchorRect.bottom + 6}px`, left: `${anchorRect.left}px` }}
            onKeyDown={handlePopoverKeyDown}
        >
            <div className="menu">
                <div className="controls-group">
                    <div className="control">
                        <input
                            ref={inputRef}
                            maxLength="1000"
                            tabIndex="2"
                            // Restored: the DOM highlight layer (painted before this input
                            // could ever steal focus — see EditableNote.jsx's beginLinkTarget/
                            // paintLinkTargetLayer) doesn't depend on the native Selection
                            // surviving focus loss the way the earlier CSS Highlight API
                            // attempt's UX concern did, so there's no reason left to withhold
                            // autofocus — the user's original complaint was that the URL field
                            // wasn't immediately typeable.
                            autoFocus={(window.innerWidth >= 1000)}
                            className="enum-input link-input"
                            type="text"
                            autoComplete="off"
                            spellCheck="false"
                            defaultValue={prefillHref}
                            placeholder={t('t.ctx.note.link_placeholder')}
                            onKeyDown={handleLinkInputKeyDown}
                            onInput={handleLinkInputInput}
                        />
                    </div>

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            className="control-button link-add"
                            type="button"
                            disabled={!canLink}
                            onClick={handleLinkApply}
                        >
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>
                    {/* <div className="control control-m4">
                        <button
                            className="control-button link-remove"
                            type="button"
                            disabled={!canLink}
                            onClick={handleLinkRemove}
                        >
                            {t('t.ctx.note.link_remove')}
                        </button>
                    </div> */}
                </div>
            </div>
        </div>,
        document.body
    );
};

// TODO: use coordinates from focused elements from Redux, not from canvas props, couse when you create new element with grabbing and have focused elements before => context menu stay at previous focused elements, not at new element
export const ContextMenu = ({ elementY, elementX, elementWidth, elementHeight, elementCenterX, left, top, isFocusedInsideElementEmpty }) => {
    const dispatch = useDispatch();

    const { canEdit: canEditByRole } = useRole();
    const isReadOnly = useSelector(selectIsDiagramReadOnly);
    // Editing requires an edit role AND the diagram not being read-only (archived).
    const canEdit = canEditByRole && !isReadOnly;

    const { t, i18n } = useTranslation();

    const { map: shortKeysMap, isMac } = useShortKeysNavigation();

    const [clickedItem, setClickedItem] = useState(null);

    const [isBtnDragMenuPressed, setIsBtnDragMenuPressed] = useState(false);
    const dragMenuRef = useRef(null);
    const dragMenuStartXYRef = useRef({ x: 0, y: 0 });
    // const dragMenuDxDyRef = useRef({ dx: 0, dy: 0 });
    const [dragMenuDxDy, setDragMenuDxDy] = useState({ dx: 0, dy: 0 });


    const [isMoreBtnClicked, setIsMoreBtnClicked] = useState(false);

    const isLightTheme = useSelector(selectResolvedTheme) === 'light';


    const isModalSupportEnabled = useSelector(selectIsModalSupportEnabled);
    const isModalDiagramDetailsEnabled = useSelector(selectIsModalDiagramDetailsEnabled);
    const isModalDiagramShareEnabled = useSelector(selectIsModalDiagramShareEnabled);
    const isBtnSearchEnabled = useSelector(selectIsBtnSearchEnabled);

    const isBtnPopupNewsEnabled = useSelector(selectIsBtnPopupNewsEnabled);

    const isBtnNewLineEnabled = useSelector(selectIsBtnNewLineEnabled);

    const boardViewMode = useSelector(selectViewMode);
    const isContextMenuShowed = useSelector(selectIsContextMenuShowed);

    const isContextMenuVisible = useSelector(selectIsContextMenuVisible);


    const focusedIds = useSelector(selectFocusedIds);

    const elements = useElements();
    const lines = useLines();

    const { cap } = usePlanGate();

    // Blocks a duplicate that would push the table count past the plan cap, opening the
    // canvas limit modal instead. `idsOrObj` is the set about to be duplicated (array of
    // ids or id→element map); only tables count toward the tables cap. Returns true when
    // the duplicate may proceed. cap === -1 means unlimited (Pro/Max).
    const canDuplicateTables = (idsOrObj) => {
        const ids = Array.isArray(idsOrObj) ? idsOrObj : Object.keys(idsOrObj || {});
        const addCount = ids.filter((id) => elements[id]?.type === 'table').length;
        if (addCount <= 0) return true;
        const max = cap('tables');
        if (max === -1) return true;
        const current = Object.values(elements).filter((el) => el?.type === 'table').length;
        if (current + addCount > max) {
            dispatch(enableCanvasPricingPlanLimit('tables'));
            return false;
        }
        return true;
    };

    // Mirrors canDuplicateTables above, but for notes — cap('notes') is backed
    // by PLAN_MATRIX (free: 20, pro/max: -1 unlimited), same as 'tables'.
    const canDuplicateNotes = (idsOrObj) => {
        const ids = Array.isArray(idsOrObj) ? idsOrObj : Object.keys(idsOrObj || {});
        const addCount = ids.filter((id) => elements[id]?.type === 'note').length;
        if (addCount <= 0) return true;
        const max = cap('notes');
        if (max === -1) return true;
        const current = Object.values(elements).filter((el) => el?.type === 'note').length;
        if (current + addCount > max) {
            dispatch(enableCanvasPricingPlanLimit('notes'));
            return false;
        }
        return true;
    };

    // Derive type-bucketed lists from the unified focusedIds.
    const focusedLines = React.useMemo(
        () => (focusedIds || []).filter((id) => lines[id]),
        [focusedIds, lines]
    );
    const focusedAreas = React.useMemo(
        () => (focusedIds || []).filter((id) => elements[id]?.type === 'area'),
        [focusedIds, elements]
    );
    const focusedElements = React.useMemo(
        () => (focusedIds || []).filter(
            (id) => elements[id] && elements[id].type !== 'area'
        ),
        [focusedIds, elements]
    );

    // Single-selection convenience values.
    const focusedLine = focusedLines.length === 1 ? lines[focusedLines[0]] : null;
    const focusedArea = focusedAreas.length === 1 ? elements[focusedAreas[0]] : null;

    // Backwards-compatible bag of focused elements (id → element) used by
    // getTableMenu / getNoteMenu / getMultiElementsMenu / getPenSegmentMenu.
    const focusedElementsById = React.useMemo(() => {
        const out = {};
        for (const id of focusedElements) {
            if (elements[id]) out[id] = elements[id];
        }
        return out;
    }, [focusedElements, elements]);

    // Same set but without pen-segments — color / border / duplicate in a
    // multi-selection apply only to tables/notes (pens have their own colour menu
    // and aren't colored/bordered/duplicated as part of a mixed selection).
    const focusedElementsByIdNoPens = React.useMemo(() => {
        const out = {};
        for (const id in focusedElementsById) {
            if (focusedElementsById[id]?.type !== 'pen-segment') out[id] = focusedElementsById[id];
        }
        return out;
    }, [focusedElementsById]);

    // Group viewMode shapes — kept for legacy menu code.
    const focusedGroupElements = React.useMemo(() => {
        const out = {};
        for (const id of focusedElements) out[id] = id;
        return out;
    }, [focusedElements]);
    const focusedGroupAreas = React.useMemo(() => {
        const out = {};
        for (const id of focusedAreas) out[id] = id;
        return out;
    }, [focusedAreas]);

    const focusedInsideElement = useSelector(selectFocusedInsideElement);

    const focusedInsideElementRef = useRef(null);

    const focusedElementsRef = useRef(null);

    const [focusedInsideElementState, setFocusedInsideElement] = useState(null);

    const menuRef = useRef();

    // NoteLinkMenu's captured-at-open args (editor/canLink/prefillHref/
    // anchorRect) — set synchronously by the note-link button's onClick
    // handler BEFORE it opens the submenu (setClickedItem), read at the
    // NoteLinkMenu render call site below. A ref, not state: NoteLinkMenu
    // itself holds no state (see its own doc comment), and writing this
    // doesn't need to trigger a render on its own — the clickedItem update
    // that follows it in the same handler already does that.
    const noteLinkMenuArgsRef = useRef({ editor: null, canLink: false, prefillHref: '', anchorRect: null });
    // NoteLinkMenu is portaled to document.body (see its own doc comment),
    // so it is NOT a DOM descendant of menuRef's node even though it's a
    // React-tree descendant of this component — the outside-click handler
    // below has to check this ref too, or every click inside the portaled
    // popover (typing in the URL input, clicking Apply) would register as
    // "outside" and immediately close the whole menu.
    const noteLinkPopoverRef = useRef(null);

    // In-editor link click popover's Edit action (EditableNote.jsx, spec 8b)
    // requests THIS component open NoteLinkMenu over a capture it already
    // made itself (over the clicked <a>'s full extent, not a live
    // selection) — see noteLinkEditRequestBridge.js's own doc comment for
    // why this needs a cross-component channel at all. Ref, not a plain
    // effect closure: the request can arrive at any point in this
    // component's lifetime, and the handler needs whichever render's
    // `handleMenuClick` is CURRENT at that moment (it closes over
    // `clickedItem`, which changes on every menu open/close) — same
    // "always-fresh delegator behind a stable ref" shape EditableNote.jsx's
    // own apiRef/registerNoteEditor bridge already uses for the same reason
    // in the opposite direction. Assigned + subscribed further down, right
    // after handleMenuClick/handleSubMenuClick are actually defined.
    const noteLinkEditRequestHandlerRef = useRef(null);

    const enumBtnRef = useRef(null);
    const defaultValueBtnRef = useRef(null);
    const generatedValueBtnRef = useRef(null);
    const checkValueBtnRef = useRef(null);

    const columnDescriptionBtnRef = useRef(null);



    const descriptionMenuInputRef = useRef(null);
    const descriptionMenuButtonRef = useRef(null);

    const enumValueMenuInputRef = useRef(null);
    const enumValueMenuButtonRef = useRef(null);

    const defaultValueMenuInputRef = useRef(null);
    const defaultValueMenuButtonRef = useRef(null);

    const generatedValueMenuInputRef = useRef(null);
    const generatedValueMenuButtonRef = useRef(null);

    const checkValueMenuInputRef = useRef(null);
    const checkValueMenuButtonRef = useRef(null);


    const [viewMode, setViewMode] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    // The selection set the "Filter Selection" button + sub-menu act on, picked by the
    // active menu: focused elements (multi), the group's elements+areas (group), or the
    // focused area's nested elements (area).
    const filterSelectionSet = React.useMemo(() => {
        if (viewMode === 'group') {
            // focusedElements/focusedAreas are id arrays — build { id: element } so the
            // type counts can read element.type (tables, notes, pens AND areas).
            const out = {};
            for (const id of [...focusedElements, ...focusedAreas]) {
                if (elements[id]) out[id] = elements[id];
            }
            return out;
        }
        if (viewMode === 'area') {
            const areaId = focusedAreas[0];
            if (!areaId) return {};
            const out = {};
            for (const id in elements) {
                if (elements[id]?.areaId === areaId && elements[id]?.type !== 'area') {
                    out[id] = elements[id];
                }
            }
            return out;
        }
        return focusedElementsById;
    }, [viewMode, focusedElements, focusedAreas, elements, focusedElementsById]);

    // A multi-selection consisting only of pen-segments gets its own menu (pen colour
    // picker instead of table colour/border, no duplicate).
    const isAllPensSelection = React.useMemo(
        () =>
            focusedElements.length > 0 &&
            focusedElements.every((id) => elements[id]?.type === 'pen-segment'),
        [focusedElements, elements]
    );

    // Sub-menus (delete confirm, colour picker, border, filter, …) drop down under the
    // trigger button that opened them. Rather than hardcode a left offset per parent
    // menu — which breaks whenever the button set or a label width changes — measure the
    // active trigger (the button carrying the `.clicked` marker) once it has rendered.
    // `menuRef` is the positioned `.context-menu`, the shared offset parent for both the
    // button and the sub-menu, so `offsetLeft` and the sub-menu's `left` line up.
    const [subMenuLeft, setSubMenuLeft] = useState(null);
    useLayoutEffect(() => {
        if (!clickedItem) { setSubMenuLeft(null); return; }
        const btn = menuRef.current?.querySelector('.control-button.clicked');
        setSubMenuLeft(btn ? btn.offsetLeft : null);
    }, [clickedItem, viewMode, focusedIds]);
    // Measured trigger position, or the given per-menu fallback when it can't be measured.
    const subMenuLeftPx = (fallbackPx, marginValue) => `${subMenuLeft != null ? subMenuLeft + marginValue : fallbackPx}px`;

    // const rombRadius = 9;
    const rombRadius = 8.25;

    let contextMenuWidth =
        // viewMode === 'pen-segment' ? 339
        viewMode === 'pen-segment' ? 255
            : viewMode === 'note' ? 421 // 636
                : viewMode === 'column'
                    ? isMoreBtnClicked ? 726 : 726 // 602
                    : viewMode === 'table' ? 302
                        : viewMode === 'area' ? 402
                            : viewMode === 'line' ? 617
                                : viewMode === 'group' ? 286
                                    : 402;


    const elementStartX = elementX;
    const elementEndX = elementX + elementWidth;
    const elementEndY = elementY + elementHeight;

    let newElementCenterX = elementCenterX;

    if (elementStartX < 0) {
        newElementCenterX = (0 + elementEndX) / 2;
    }

    if (elementEndX > window.innerWidth) {
        newElementCenterX = (elementStartX + window.innerWidth) / 2;
    }

    if (elementStartX < 0 && elementEndX > window.innerWidth) {
        newElementCenterX = (0 + window.innerWidth) / 2;
    }


    const isNotElementInViewPort =
        elementStartX > window.innerWidth ||
        elementEndX < 0 ||
        elementY > window.innerHeight ||
        elementEndY < 0;


    // const topMin = 70;
    const topMin = isBtnPopupNewsEnabled ? 70 + 54 + 4 : 70 + 4;
    let topOptimal = top < topMin ? topMin : top + 4;

    const leftMin = 70;
    const leftMax = window.innerWidth - contextMenuWidth - 15;

    let positionLeft = newElementCenterX - (contextMenuWidth / 2) - 1;

    positionLeft = positionLeft < leftMin ? leftMin : positionLeft;
    positionLeft = positionLeft + contextMenuWidth > window.innerWidth ? leftMax : positionLeft;

    const positionMenuStyle = {
        left: `${positionLeft + dragMenuDxDy.dx || 0}px`,
        top: `${topOptimal + dragMenuDxDy.dy || 0}px`,
    };

    // show menu button
    const wrappedMenu = (
        <div
            className={`menu`}
            style={{ left: `${positionLeft || 0}px`, top: `${topOptimal || 0}px` }}
        >
            <div className="controls-group">
                <div className="control">
                    <button
                        className="control-button"
                        type="button"
                        title="0-Movable Columns"
                        aria-label="0-Movable Columns"
                        aria-pressed="false"
                        onClick={() => dispatch(showContextMenu({}))}
                    >
                        <ShowMenuIcon />
                    </button>
                </div>
            </div>
        </div>
    );

    // hide menu button
    // const HideMenuPart = <div className="control">
    // 	<button
    // 		className='control-button'
    // 		type="button"
    // 		title="0-Movable Columns"
    // 		aria-label="0-Movable Columns"
    // 		aria-pressed="false"
    // 		onClick={() => dispatch(hideContextMenu())}
    // 	>
    // 		<HideMenuIcon />
    // 	</button>
    // </div>;

    // visibility/opacity menu button
    const VisibilityMenuPart = (
        <div className="control">
            <Tooltip.Root delayDuration={600} disableHoverableContent>
                <Tooltip.Trigger asChild>
                    <button
                        className={`control-button visibility-btn ${!isContextMenuVisible ? 'selected' : ''}`}
                        type="button"
                        aria-pressed="false"
                        onClick={() => dispatch(toggleVisibilityContextMenu())}
                    >
                        <VisibilityNewIcon />
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        className="tooltip-content tooltip-controls wo-shortcut"
                        side="top"
                        align="center"
                        sideOffset={10}
                    >
                        <span>
                            {isContextMenuVisible
                                ? t('t.ctx.base.hide_menu')
                                : t('t.ctx.base.show_menu')}
                        </span>
                        {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </div>
    );

    // drag menu button
    const DragMenuPart = (
        <div className="control drag-menu">
            {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                <Tooltip.Trigger asChild> */}
            <button
                ref={dragMenuRef}
                className={`control-button ${isBtnDragMenuPressed || (dragMenuDxDy.dx || dragMenuDxDy.dy) ? 'selected' : ''}`}
                type="button"
                aria-pressed="false"
                // onClick={() => {
                // 	handleMenuClick();
                // }}
                style={{ width: '24px' }}
            >
                <DragMenuIcon />
            </button>
            {/* </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        className="tooltip-content tooltip-controls"
                        side="top"
                        align="center"
                        sideOffset={10}
                    >
                        <span>Drag Menu</span>

                        <Tooltip.Arrow className="tooltip-arrow" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root> */}
        </div>
    );

    // NOTE MENU
    // + drag menu
    // -----------------
    //4 + type/size (/NOTIFICATION/REMINDER/MESSAGE/TODO/NOTE/question/description/need decision/change request/need review)
    // - size (S/M/L)
    // -----------------
    // - font (inte/roboto/monospace/etc)
    // - font-size (14 + <|>)
    // -----------------
    // + font-style (bold/italic/underscore/crosscore/etc)
    // + font-monospace
    // + alignment (left/center/right)
    // -----------------
    // + background-color
    // + border
    // -----------------
    //3 + link (insert link)
    //3 + tags
    // -----------------
    // - author (author name - hide/show) (with avatar/without)
    // - date (show date/time)
    // - add emoji/reaction (likes/smiles/weird/new-img-symbols)
    // -----------------
    // + comment
    // + copy
    // + delete
    // + hide menu

    // Routes a note style/list toggle to the live rich-text editor (if the note's
    // editor overlay is open) or falls back to mutating the whole-text HTML directly
    // via the htmlNoteOps helpers — mirrors EditableNote's own collapsed-caret path.
    const applyNoteStyle = (note, style) => {
        const editor = getNoteEditor(note.id);
        if (editor) { editor.execStyle(style); editor.focus(); return; }
        setNoteText(getDiagramDoc(), note.id, toggleStyleAll(note.text ?? '', style));
    };
    const applyNoteList = (note, kind) => {
        const editor = getNoteEditor(note.id);
        if (editor) { editor.execList(kind); editor.focus(); return; }
        setNoteText(getDiagramDoc(), note.id, toggleListAll(note.text ?? '', kind));
    };

    // note menu
    const getNoteMenu = (focusedElements) => {
        const keys = Object.keys(focusedElements);

        if (!keys.length) {
            return;
        }

        const note = focusedElements[keys[0]];
        const { border, color } = note;
        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        if (!color) {
            colorCounter.none++;
        } else {
            colorCounter[color]++;
        }

        const colorName = colorCounter.blue
            ? 'blue'
            : colorCounter.green
                ? 'green'
                : colorCounter.red
                    ? 'red'
                    : colorCounter.purple
                        ? 'purple'
                        : colorCounter.violet
                            ? 'violet'
                            : null;

        return (
            <div className={`menu`} >
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* font style */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            {/* <div className="control" style={{ marginRight: '-1px' }}> */}
                            <div className="control" style={{ marginLeft: '1px', marginRight: '0px' }}>
                                <button
                                    className={`control-button ${clickedItem == 'font-styles' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('font-styles');
                                    }}
                                >
                                    <BoldTextIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.font_style')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* font size */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
						<Tooltip.Trigger asChild>

							<div className="control">
								<button
									className='control-button button-font-size'
									type="button"
									aria-pressed="false"
									onClick={() => {
										handleMenuClick('element-borders');
									}}
								>
									14
								</button>
							</div>
						</Tooltip.Trigger>
						<Tooltip.Portal>
							<Tooltip.Content
								className="tooltip-content tooltip-controls"
								side="top"
								align="center"
								sideOffset={10}
							>
								<span>Font Size</span>
							</Tooltip.Content>
						</Tooltip.Portal>
					</Tooltip.Root> */}

                    {/* alignment */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginLeft: '1px', marginRight: '1px' }}>
                                <button
                                    className={`control-button
                                        ${(['center', 'right'].includes(note.alignmentHorizontal) || ['center', 'bottom'].includes(note.alignmentVertical)) ? 'selected' : ''}
                                        ${clickedItem == 'align' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('align');
                                    }}
                                >
                                    {note.alignmentHorizontal ===
                                        'right' ? (
                                        <AlignRightIconNew />
                                    ) : note.alignmentHorizontal ===
                                        'center' ? (
                                        <AlignCenterIconNew />
                                    ) : (
                                        <AlignLeftIconNew />
                                    )}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.alignment')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* link */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '1px' }}>
                                <button
                                    className={`control-button ${clickedItem === 'note-link' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    // The button press itself must not steal focus from the note's
                                    // contenteditable — that would drop the live text selection
                                    // before beginLinkTarget (right below) even runs.
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(event) => {
                                        // Capture-at-open, only when actually OPENING (not when this
                                        // click is toggling an already-open submenu closed — nothing
                                        // to (re-)capture against by then, and doing so would just
                                        // capture/paint whatever the live selection happens to be at
                                        // that moment, which is unrelated to what's already displayed).
                                        // NoteLinkMenu itself holds no state (see its own doc comment) —
                                        // everything it needs is decided here, once, and handed down as
                                        // props.
                                        if (clickedItem !== 'note-link') {
                                            const editor = getNoteEditor(note.id);
                                            // Read BEFORE beginLinkTarget (next line): beginLinkTarget
                                            // paints its own highlight-layer sibling (see EditableNote.jsx's
                                            // own doc comment), never touching the note's own text markup
                                            // or the live Selection, but ordering this first keeps the two
                                            // reads pinned to the exact same live-selection moment regardless.
                                            const selectionStyles = editor ? editor.getSelectionStyles() : null;
                                            const canLink = !!editor && editor.beginLinkTarget();
                                            // Anchor: the captured Range's own bounding rect — a selection's
                                            // box, or the caret position for a collapsed capture (the
                                            // insert-new-link flow; beginLinkTarget now captures a bare
                                            // caret too). Falls back to this BUTTON's own rect when nothing
                                            // was captured at all (no caret in the editor — canLink false)
                                            // or the collapsed rect is unusable (all-zero browser quirk,
                                            // getLinkTargetRect returns null for it).
                                            const rangeRect = canLink && editor?.getLinkTargetRect ? editor.getLinkTargetRect() : null;
                                            const buttonRect = event.currentTarget.getBoundingClientRect();
                                            noteLinkMenuArgsRef.current = {
                                                editor,
                                                canLink,
                                                prefillHref: selectionStyles?.linkHref || '',
                                                anchorRect: rangeRect || {
                                                    top: buttonRect.bottom, bottom: buttonRect.bottom,
                                                    left: buttonRect.left, right: buttonRect.right,
                                                },
                                            };
                                        }
                                        handleMenuClick('note-link');
                                    }}
                                >
                                    <LinkIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.link')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* Font Monospace */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '0px' }}>
                                <button
                                    className={`control-button ${note.isFontMonospace ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        setNoteFontMonospace(getDiagramDoc(), note.id);
                                    }}
                                >
                                    <FontMonospaceIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.monospace')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>


                    {/* bulleted list */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isListAll(note.text ?? '', 'bullet') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        applyNoteList(note, 'bullet');
                                    }}
                                >
                                    <svg width="22px" height="23px" viewBox="0 0 24 24" fill="none">
                                        <circle cx="4" cy="6" r="1.5" fill="#ffffff" />
                                        <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
                                        <circle cx="4" cy="18" r="1.5" fill="#ffffff" />
                                        <path d="M9 6H21M9 12H21M9 18H21" stroke="#ffffff" strokeWidth="1.5px" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.bulleted_list')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* type */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick();
                                    }}
                                >
                                    <NoteSmallIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Type</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* color-picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`
                                        control-button
                                        ${colorName ? 'selected' : ''}
                                        ${clickedItem == 'color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={colorName || 'grey'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* <div className="control-divider"></div> */}

                    {/* tags */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginLeft: '1px', marginRight: '0px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick();
                                    }}
                                >
                                    <TagIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Tags</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* author
					<Tooltip.Root delayDuration={600} disableHoverableContent>
						<Tooltip.Trigger asChild>

							<div className="control">
								<button
									className='control-button'
									type="button"
									aria-pressed="false"
									onClick={() => {
										handleMenuClick('element-borders');
									}}
								>
									Author
								</button>
							</div>
						</Tooltip.Trigger>
						<Tooltip.Portal>
							<Tooltip.Content
								className="tooltip-content tooltip-controls"
								side="top"
								align="center"
								sideOffset={10}
							>
								<span>Author</span>
							</Tooltip.Content>
						</Tooltip.Portal>
					</Tooltip.Root> */}

                    {/* date
					<Tooltip.Root delayDuration={600} disableHoverableContent>
						<Tooltip.Trigger asChild>

							<div className="control">
								<button
									className='control-button'
									type="button"
									aria-pressed="false"
									onClick={() => {
										handleMenuClick('element-borders');
									}}
								>
									Date
								</button>
							</div>
						</Tooltip.Trigger>
						<Tooltip.Portal>
							<Tooltip.Content
								className="tooltip-content tooltip-controls"
								side="top"
								align="center"
								sideOffset={10}
							>
								<span>Date</span>
							</Tooltip.Content>
						</Tooltip.Portal>
					</Tooltip.Root> */}

                    {/* reaction
					<Tooltip.Root delayDuration={600} disableHoverableContent>
						<Tooltip.Trigger asChild>

							<div className="control">
								<button
									className='control-button'
									type="button"
									aria-pressed="false"
									onClick={() => {
										handleMenuClick('element-borders');
									}}
								>
									Reaction
								</button>
							</div>
						</Tooltip.Trigger>
						<Tooltip.Portal>
							<Tooltip.Content
								className="tooltip-content tooltip-controls"
								side="top"
								align="center"
								sideOffset={10}
							>
								<span>Reaction</span>
							</Tooltip.Content>
						</Tooltip.Portal>
					</Tooltip.Root> */}

                    <div className="control-divider"></div>

                    {/* copy */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        if (!canDuplicateTables(focusedElementsByIdNoPens) || !canDuplicateNotes(focusedElementsByIdNoPens)) return;
                                        const newIds = duplicateElements(getDiagramDoc(), focusedElementsByIdNoPens);
                                        if (newIds.length) {
                                            dispatch(updateFocusedIds(newIds));
                                            // Header focus (inline rename) only for a single duplicated element.
                                            if (newIds.length === 1) {
                                                dispatch(updateFocusedInsideElement(newIds[0], null, null, 'header'));
                                            }
                                        }
                                    }}
                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${clickedItem === 'delete-element' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        deleteElements(getDiagramDoc(), focusedElements);
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete')}</span>
                                <span className="tooltip-shortcut">Del</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // table menu
    const getTableMenu = (focusedElements) => {
        const keys = Object.keys(focusedElements);

        if (!keys.length) {
            return;
        }

        const table = focusedElements[keys[0]];
        const { border, color } = table;

        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        if (!color) {
            colorCounter.none++;
        } else {
            colorCounter[color]++;
        }

        const colorName = colorCounter.blue
            ? 'blue'
            : colorCounter.green
                ? 'green'
                : colorCounter.red
                    ? 'red'
                    : colorCounter.purple
                        ? 'purple'
                        : colorCounter.violet
                            ? 'violet'
                            : null;

        return (
            <div className={`menu`} >
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* Key */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                    ${(focusedInsideElementRef.current?.insideElement?.isPK || focusedInsideElementRef.current?.insideElement?.isFK) ? 'selected' : ''}
                                    ${clickedItem == 'key' ? 'clicked' : ''}
                                `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('key');
                                    }}
                                >
                                    <KeyIcon
                                        isDark={!isLightTheme}
                                        isPK={
                                            focusedInsideElementRef.current
                                                ?.insideElement?.isPK
                                        }
                                        isFK={
                                            focusedInsideElementRef.current
                                                ?.insideElement?.isFK
                                        }
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Key</span>
                                    <span className="tooltip-shortcut">{shortKeysMap?.CTRL}</span>
                                <span className="tooltip-shortcut">
                                    1
                                </span>

                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* Index */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button index
                                    ${focusedInsideElementRef.current?.insideElement?.index?.type ? 'selected' : ''}
                                    ${clickedItem == 'index' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('index');
                                    }}
                                >
                                    <svg
                                        className={`context-menu-column-index ${focusedInsideElementRef.current?.insideElement?.index?.type} ${focusedInsideElementRef.current?.insideElement?.index?.type ? 'selected' : ''}`}
                                    >
                                        <polygon
                                            points={`${rombRadius + 1},1 ${rombRadius * 2 + 1
                                                },${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1
                                                } 1,${rombRadius + 1}`}
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Index</span>
                                    <span className="tooltip-shortcut">{shortKeysMap?.CTRL}</span>
                                <span className="tooltip-shortcut">
                                   2
                                </span>

                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* <div className="control-divider"></div> */}


                    {/* Indexes */}

                    {/* Migration */}


                    {/* color-picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`
                                        control-button
                                        ${colorName ? 'selected' : ''}
                                        ${clickedItem == 'color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={colorName || 'grey'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* border */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`
                                        control-button line-connector border
                                        ${table?.border ? 'selected' : ''}
                                        ${clickedItem == 'element-borders' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('element-borders');
                                    }}
                                >
                                    <ElementBorderIcon
                                        isDashes={table?.border === 'dashes'}
                                        isDots={table?.border === 'dots'}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.border')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* Description */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${table?.description ? 'selected' : ''} ${clickedItem == 'table-description' ? 'clicked' : ''} `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('table-description');
                                    }}
                                >
                                    <ElementDescriptionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.description')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>


                    {/* Auto Layout element */}

                    {/*
                        <div className="control">
                            <button
                                className='control-button'
                                type="button"
                                title="2-Auto Layout"
                                aria-label="Auto Layout"
                                aria-pressed="false"
                                onClick={() => dispatch(showTablesChanges({}))}
                            >
                                <AutoLayoutIcon />
                            </button>
                        </div>
                    */}

                    {/* Auto Resize element */}
                    {/* show only if table is already resized */}

                    {/*
                        <div className="control">
                            <button
                                className='control-button'
                                type="button"
                                title="Auto Resize"
                                aria-label="Auto Resize"
                                aria-pressed="false"
                                onClick={() => dispatch(autoResizeElements())}
                            >
                                <FitToZoomIcon />
                            </button>
                        </div>
			        */}

                    {/* Duplicate */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        if (!canDuplicateTables(focusedElementsByIdNoPens)) return;
                                        const newIds = duplicateElements(getDiagramDoc(), focusedElementsByIdNoPens);
                                        if (newIds.length) {
                                            dispatch(updateFocusedIds(newIds));
                                            // Header focus (inline rename) only for a single duplicated element.
                                            if (newIds.length === 1) {
                                                dispatch(updateFocusedInsideElement(newIds[0], null, null, 'header'));
                                            }
                                        }
                                    }}
                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>

                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                                {/* <span className="tooltip-shortcut">C</span> */}
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* Delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${clickedItem == 'delete-element' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('delete-element');
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete_confirm')}</span>
                                {focusedInsideElement && focusedInsideElement?.insideElementType === 'header'
                                    ? (<><span className="tooltip-shortcut">{shortKeysMap?.CTRL} + Del</span></>)
                                    : (<span className="tooltip-shortcut">Del</span>)}
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        )
    };

    // multi elements menu
    const getMultiElementsMenu = (focusedElements) => {
        const elementsCount = Object.keys(focusedElements).length;
        if (!elementsCount) {
            return;
        }

        let borderCounter = { none: 0, solid: 0, dashes: 0, dots: 0 };
        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        for (let key in focusedElements) {
            const element = focusedElements[key];

            // border section
            if (!element?.border) {
                borderCounter.none++;
            } else {
                borderCounter[element?.border]++;
            }

            // color section
            if (!element?.color) {
                colorCounter.none++;
            } else {
                colorCounter[element?.color]++;
            }
        }

        const colorName =
            colorCounter.blue == elementsCount
                ? 'blue'
                : colorCounter.green == elementsCount
                    ? 'green'
                    : colorCounter.red == elementsCount
                        ? 'red'
                        : colorCounter.purple == elementsCount
                            ? 'purple'
                            : colorCounter.violet == elementsCount
                                ? 'violet'
                                : null;

        return (
            <div className={`menu`}>
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* Filter Selection */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button filter-selection ${clickedItem === 'filter-selection' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed={clickedItem === 'filter-selection'}
                                    onClick={() => handleMenuClick('filter-selection')}
                                >
                                    {filterSelectionLabel(filterSelectionSet)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.multi.filter')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* Group Selection */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        addAreaForFocusedElements(getDiagramDoc(), {
                                            focusedElementsIds: Object.keys(focusedElements),
                                            areaProps: { id: nanoid() },
                                        });
                                    }}
                                >
                                    <TableGroupNewSmallIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.multi.create_area')}</span>
                                {/* <span>Group Selection</span> */}
                                {/* <span className="tooltip-shortcut">G</span> */}
                                <span className="tooltip-shortcut">A</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* color picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                        ${colorName ? 'selected' : ''}
                                        ${clickedItem == 'color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={colorName || 'grey'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* border */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border
                                        ${(borderCounter.solid == elementsCount ||
                                            borderCounter.dashes == elementsCount ||
                                            borderCounter.dots == elementsCount) ? 'selected' : ''}
                                        ${clickedItem == 'element-borders' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('element-borders');
                                    }}
                                >
                                    <ElementBorderIcon
                                        isDashes={borderCounter.dashes == elementsCount}
                                        isDots={borderCounter.dots == elementsCount}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.border')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* duplicate */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        if (!canDuplicateTables(focusedElementsByIdNoPens) || !canDuplicateNotes(focusedElementsByIdNoPens)) return;
                                        const newIds = duplicateElements(getDiagramDoc(), focusedElementsByIdNoPens);
                                        if (newIds.length) {
                                            dispatch(updateFocusedIds(newIds));
                                            // Header focus (inline rename) only for a single duplicated element.
                                            if (newIds.length === 1) {
                                                dispatch(updateFocusedInsideElement(newIds[0], null, null, 'header'));
                                            }
                                        }
                                    }}
                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                                {/* <span className="tooltip-shortcut">C</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                    ${clickedItem == 'delete-element' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick('delete-element');
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete_confirm')}</span>
                                <span className="tooltip-shortcut">Del</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // multi-selection of only pen-segments — filter, create-area, pen colour, delete.
    const getMultiPenSegmentsMenu = (focusedElements) => {
        const elementsCount = Object.keys(focusedElements).length;
        if (!elementsCount) return;

        // Common pen colour for the swatch (null when the pens differ).
        const penColorCounter = {};
        for (const key in focusedElements) {
            const c = focusedElements[key]?.color || 'yellow';
            penColorCounter[c] = (penColorCounter[c] || 0) + 1;
        }
        const penColorName =
            Object.keys(penColorCounter).length === 1 ? Object.keys(penColorCounter)[0] : null;
        const penColorDisplay = penColorName === 'yellow' ? '#ffc65e' : (penColorName || 'grey');

        return (
            <div className={`menu`}>
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* Filter Selection */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button filter-selection ${clickedItem === 'filter-selection' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed={clickedItem === 'filter-selection'}
                                    onClick={() => handleMenuClick('filter-selection')}
                                >
                                    {filterSelectionLabel(filterSelectionSet)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className="tooltip-content tooltip-controls wo-shortcut" side="top" align="center" sideOffset={10}>
                                <span>{t('t.ctx.multi.filter')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* Create area for selected pens */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        addAreaForFocusedElements(getDiagramDoc(), {
                                            focusedElementsIds: Object.keys(focusedElements),
                                            areaProps: { id: nanoid() },
                                        });
                                    }}
                                >
                                    <TableGroupNewSmallIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className="tooltip-content tooltip-controls" side="top" align="center" sideOffset={10}>
                                <span>{t('t.ctx.multi.create_area')}</span>
                                <span className="tooltip-shortcut">A</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* pen colour picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                        ${penColorName ? 'selected' : ''}
                                        ${clickedItem == 'pen-color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('pen-color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={penColorDisplay} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className="tooltip-content tooltip-controls wo-shortcut" side="top" align="center" sideOffset={10}>
                                <span>{t('t.ctx.base.color')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                    ${clickedItem == 'delete-element' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick('delete-element');
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content className="tooltip-content tooltip-controls" side="top" align="center" sideOffset={10}>
                                <span>{t('b.delete_confirm')}</span>
                                <span className="tooltip-shortcut">Del</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // group menu (areas + elements)
    const getGroupMenu = (focusedGroupElements, focusedGroupAreas) => {
        const elementsCount = Object.keys(focusedGroupElements).length;
        const areasCount = Object.keys(focusedGroupAreas).length;
        if (!areasCount && !elementsCount) {
            return;
        }

        return (
            <div className={`menu`}>
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* Filter Selection */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button filter-selection ${clickedItem === 'filter-selection' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed={clickedItem === 'filter-selection'}
                                    onClick={() => handleMenuClick('filter-selection')}
                                >
                                    {filterSelectionLabel(filterSelectionSet)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.multi.filter')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* duplicate */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        const doc = getDiagramDoc();
                                        if (!canDuplicateTables(focusedElements) || !canDuplicateNotes(focusedElements)) return;
                                        const { newElementIds, newAreaIds } = duplicateGroup(doc, {
                                            elementIds: focusedElements,
                                            areaIds: focusedAreas,
                                        });
                                        const allNewIds = [...newElementIds, ...newAreaIds];
                                        if (allNewIds.length) {
                                            dispatch(updateFocusedIds(allNewIds));
                                        }
                                    }}
                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                                {/* <span className="tooltip-shortcut">C</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button
                                    ${clickedItem == 'delete-element' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('delete-element');
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete_confirm')}</span>
                                <span className="tooltip-shortcut">Del</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // pen menu
    const getPenSegmentMenu = (focusedElements) => {
        const keys = Object.keys(focusedElements);
        if (!keys.length) {
            return;
        }

        const segment = focusedElements[keys[0]];
        const { border, color } = segment;
        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        if (!color) {
            colorCounter.none++;
        } else {
            colorCounter[color]++;
        }

        const colorName = colorCounter.blue
            ? 'blue'
            : colorCounter.green
                ? 'green'
                : colorCounter.red
                    ? 'red'
                    : colorCounter.purple
                        ? 'purple'
                        : colorCounter.violet
                            ? 'violet'
                            : null;

        // The pen palette is yellow / red / lightgreen, so show the segment's actual
        // colour on the button (colorName only covers the table palette).
        const penColorDisplay = color === 'red'
            ? 'red'
            : color === 'lightgreen'
                ? 'lightgreen'
                : '#ffc65e';

        return (
            <div className={`menu`}>
                <div className="controls-group">

                    {DragMenuPart}

                    <div className="control-divider"></div>

                    {/* color-picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    disabled={!canEdit}
                                    className={`control-button
                                        // ${colorName ? 'selected' : ''}
                                        selected
                                        ${clickedItem == 'pen-color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick('pen-color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={penColorDisplay} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* border */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    disabled
                                    className={`control-button disabled line-connector border
                                        ${segment?.border ? 'selected' : ''}
                                        ${clickedItem == 'element-borders' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('element-borders');
                                    }}
                                >
                                    <ElementBorderIcon
                                        isDashes={segment?.border === 'dashes'}
                                        isDots={segment?.border === 'dots'}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.border')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* <div className="control-divider"></div> */}

                    {/* description */}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${segment?.description ? 'selected' : ''} ${clickedItem == 'pen-description' ? 'clicked' : ''} `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('pen-description');
                                    }}
                                >
                                    <ElementDescriptionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.description')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    <div className="control-divider"></div>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${clickedItem === 'delete-element' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        deleteElements(getDiagramDoc(), focusedElements);
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete')}</span>
                                <span className="tooltip-shortcut">Del</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // area menu
    const getAreaMenu = (focusedArea) => {
        if (!focusedArea) {
            return;
        }

        const { color } = focusedArea;
        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        if (!color) {
            colorCounter.none++;
        } else {
            colorCounter[color]++;
        }

        const colorName = colorCounter.blue
            ? 'blue'
            : colorCounter.green
                ? 'green'
                : colorCounter.red
                    ? 'red'
                    : colorCounter.purple
                        ? 'purple'
                        : colorCounter.violet
                            ? 'violet'
                            : null;

        return (
            <div className={`menu`} >
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* filter selection */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button filter-selection ${clickedItem === 'filter-selection' ? 'clicked' : ''}`}
                                    type="button"
                                    aria-pressed={clickedItem === 'filter-selection'}
                                    onClick={() => handleMenuClick('filter-selection')}
                                >
                                    {filterSelectionLabel(filterSelectionSet)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.area.filter')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* color-picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorName ? 'selected' : ''}
                                        ${clickedItem == 'color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={colorName || 'grey'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* border */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border ${['dashes', 'dots'].includes(focusedArea?.border) ? 'selected' : ''}
                                        ${clickedItem == 'element-borders' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('element-borders');
                                    }}
                                >
                                    <ElementBorderIcon
                                        isDashes={focusedArea?.border === 'dashes'}
                                        isDots={focusedArea?.border === 'dots'}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.border')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* description */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${focusedArea.description ? 'selected' : ''} ${clickedItem == 'area-description' ? 'clicked' : ''} `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('area-description');
                                    }}
                                >
                                    <ElementDescriptionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.description')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* copy */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        duplicateArea(getDiagramDoc(), focusedAreas[0]);
                                    }}
                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                                {/* <span className="tooltip-shortcut">C</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        deleteArea(getDiagramDoc(), focusedAreas[0]);
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete')}</span>
                                <span className="tooltip-shortcut">Del</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // column menu
    const getColumnMenu = () => {
        if (!focusedInsideElementRef.current || !focusedInsideElement) {
            return;
        }

        const column = focusedInsideElementRef.current;

        if (!column?.elementId || !column?.insideElementId) {
            return;
        }

        const colData = elements[column?.elementId]?.columns?.[column?.insideElementId];

        const isMoreOptionSelected =
            colData?.generatedValue ||
            colData?.checkValue;

        return (
            <div className={`menu`} >
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* add column below */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control control-m-r-1">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        const newColId = nanoid();
                                        const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                        const curIdx = colOrder.indexOf(column?.insideElementId);
                                        const newColIdx = curIdx >= 0 ? curIdx + 1 : colOrder.length;
                                        addColumn(getDiagramDoc(), { tableId: column?.elementId, mode: 'below', refColumnId: column?.insideElementId, column: { id: newColId, name: 'column_new', type: 'int' } });
                                        dispatch(updateFocusedInsideElement(column?.elementId, newColId, newColIdx, 'column'));
                                    }}
                                >
                                    <InsertBeforeIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.add_col_below')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + Enter</span>
                                {/* <span className="tooltip-shortcut">Enter</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* add column above */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control control-m-l-1">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        const newColId = nanoid();
                                        const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                        const curIdx = colOrder.indexOf(column?.insideElementId);
                                        const newColIdx = curIdx >= 0 ? curIdx : 0;
                                        addColumn(getDiagramDoc(), { tableId: column?.elementId, mode: 'above', refColumnId: column?.insideElementId, column: { id: newColId, name: 'column_new', type: 'int' } });
                                        dispatch(updateFocusedInsideElement(column?.elementId, newColId, newColIdx, 'column'));
                                    }}
                                >
                                    <InsertAfterIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.add_col_above')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* key */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control margin-right-small">
                                <button
                                    className={`control-button
                                    ${(colData?.isPK || colData?.isFK) ? 'selected' : ''}
                                    ${clickedItem == 'key' ? 'clicked' : ''}
                                `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('key');
                                    }}
                                >
                                    <KeyIcon
                                        isDark={!isLightTheme}
                                        isPK={colData?.isPK}
                                        isFK={colData?.isFK}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.key')}</span>
                                {/* <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 1
                                </span>
                                <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 2
                                </span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* index */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control margin-left-small">
                                <button
                                    className={`control-button index
                                    ${colData?.indexType ? 'selected' : ''}
                                    ${clickedItem == 'index' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('index');
                                    }}
                                >
                                    <svg className={`context-menu-column-index ${colData?.indexType} ${colData?.indexType ? 'selected' : ''}`} >
                                        <polygon
                                            points={`${rombRadius + 1},1 ${rombRadius * 2 + 1},${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1} 1,${rombRadius + 1}`}
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.index')}</span>
                                {/* <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 2
                                </span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* auto-increment */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colData?.isAutoIncrement ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        setColumnAutoIncrement(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                    }}
                                >
                                    <div className={`context-menu-column-not-null autoincrement ${colData?.isAutoIncrement ? 'selected' : ''}`} >
                                        <span>+</span>
                                    </div>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.increment')}</span>
                                {/* <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + 3</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* enum */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    ref={enumBtnRef}
                                    className={`control-button ${colData?.isEnum ? 'selected' : ''}
                                    ${clickedItem == 'enum' ? 'clicked' : ''}
                                `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick();
                                        handleMenuClick('enum');
                                    }}
                                >
                                    <div
                                        className={`context-menu-column-not-null enum
                                        ${colData?.isEnum ? 'selected' : ''}
                                        ${clickedItem == 'enum' ? 'clicked' : ''}
                                    `}
                                    >
                                        <span>E</span>
                                    </div>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.enum')}</span>
                                {/* <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + 4</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* not null */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colData?.isNotNull ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        setColumnNotNull(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                    }}
                                >
                                    <div className={`context-menu-column-not-null ${colData?.isNotNull ? 'selected' : ''}`}>
                                        <span>NN</span>
                                    </div>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.not_null')}</span>
                                {/* <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + 3</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* default value */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    ref={defaultValueBtnRef}
                                    className={`control-button
                                    ${colData?.defaultValue ? 'selected' : ''}
                                    ${clickedItem == 'default-value' ? 'clicked' : ''}
                                `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick();
                                        handleMenuClick('default-value');
                                    }}
                                >
                                    <div
                                        className={`context-menu-column-not-null default_value
                                        ${colData?.defaultValue ? 'selected' : ''}
                                        ${clickedItem == 'default-value' ? 'clicked' : ''}
                                    `}
                                    >
                                        <span>D</span>
                                    </div>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.default_value')}</span>
                                {/* <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + 5</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* <div className="control-divider"></div> */}

                    {/* more functions */}
                    {/* {
                        !isMoreBtnClicked && !isMoreOptionSelected &&

                        <Fragment>

                            <div className="control-divider"></div>

                            <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control">
                                        <button
                                            className={`control-button more-button`}
                                            type="button"
                                            aria-pressed="false"
                                            onClick={() => {
                                                handleMenuClick();
                                                setIsMoreBtnClicked(true);
                                            }}
                                        >...</button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls wo-shortcut"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>{t('t.ctx.tab_col.more_functions')}</span>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                            <div className="control-divider"></div>
                        </Fragment>
                    } */}


                    {
                        // (isMoreBtnClicked || isMoreOptionSelected) &&
                        <Fragment>

                            {/* Generated Value */}
                            <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control">
                                        <button
                                            ref={generatedValueBtnRef}
                                            className={`control-button
                                                ${colData?.generatedValue ? 'selected' : ''}
                                                ${clickedItem == 'generated-value' ? 'clicked' : ''}
                                            `}
                                            type="button"
                                            aria-pressed="false"
                                            onClick={() => {
                                                handleMenuClick('generated-value');
                                            }}
                                        >
                                            {/* <GeneratedColumnIcon /> */}
                                            <div
                                                className={`context-menu-column-not-null generated_value
                                                    ${colData?.generatedValue ? 'selected' : ''}
                                                    ${clickedItem == 'generated-value' ? 'clicked' : ''}
                                                `}
                                            >
                                                <span style={{ fontSize: '12px' }}>G</span>
                                            </div>
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls wo-shortcut"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>{t('t.ctx.tab_col.generated_value')}</span>
                                        {/* <span className="tooltip-shortcut">
                                            {shortKeysMap?.CTRL} + 6
                                        </span> */}

                                        {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            {/* <div className="control-divider"></div> */}

                            {/* Check Expression */}
                            <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control">
                                        <button
                                            ref={checkValueBtnRef}
                                            className={`control-button
                                                ${colData?.checkValue ? 'selected' : ''}
                                                ${clickedItem == 'check-value' ? 'clicked' : ''}
                                            `}
                                            type="button"
                                            aria-pressed="false"
                                            onClick={() => {
                                                handleMenuClick('check-value');
                                            }}
                                        >
                                            <CheckExpressionIcon />
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls wo-shortcut"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>{t('t.ctx.tab_col.check')}</span>
                                        {/* <span className="tooltip-shortcut">
                                            {shortKeysMap?.CTRL} + 7
                                        </span> */}

                                        {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            {/* more functions */}
                            {/* {
                                !isMoreBtnClicked && !isMoreOptionSelected &&

                                <Fragment>

                                    <div className="control-divider"></div>

                                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                                        <Tooltip.Trigger asChild>
                                            <div className="control">
                                                <button
                                                    className={`control-button more-button`}
                                                    type="button"
                                                    aria-pressed="false"
                                                    onClick={() => {
                                                        handleMenuClick();
                                                        setIsMoreBtnClicked(true);
                                                    }}
                                                >...</button>
                                            </div>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content
                                                className="tooltip-content tooltip-controls wo-shortcut"
                                                side="top"
                                                align="center"
                                                sideOffset={10}
                                            >
                                                <span>{t('t.ctx.tab_col.more_functions')}</span>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                    <div className="control-divider"></div>
                                </Fragment>
                            } */}


                            {/* On Update Trigger */}
                            {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control">
                                        <button
                                            className={`control-button letter ${focusedInsideElementRef.current
                                                ?.insideElement?.index?.type ? 'selected' : ''}`}
                                            type="button"
                                            aria-pressed="false"
                                            onClick={() => {
                                                handleMenuClick('index');
                                            }}
                                        >
                                            <TriggerIcon />
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>On Update / On Delete Trigger</span>
                                        <span className="tooltip-shortcut">
                                            {shortKeysMap?.CTRL} + 8
                                        </span>

                                        <Tooltip.Arrow className="tooltip-arrow" />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root> */}

                            <div className="control-divider"></div>

                            {/* Move Column Down */}
                            <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control control-m-r-1">
                                        <button
                                            className="control-button"
                                            type="button"
                                            aria-pressed="false"
                                            disabled={!canEdit}
                                            onClick={() => {
                                                if (!canEdit) return;
                                                handleMenuClick();
                                                const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                                const curIdx = colOrder.indexOf(column?.insideElementId);
                                                moveColumnBelow(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                                if (curIdx >= 0 && curIdx < colOrder.length - 1) {
                                                    dispatch(updateFocusedInsideElement(column?.elementId, column?.insideElementId, curIdx + 1, 'column'));
                                                }
                                            }}
                                        >
                                            <DraggableColumnsReverseIcon />
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>{t('t.ctx.tab_col.move_col_down')}</span>

                                        <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + <span className='text_shift'>{shortKeysMap?.SHIFT}</span> + Down</span>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            {/* Move Column Up */}
                            <Tooltip.Root delayDuration={600} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control control-m-l-1">
                                        <button
                                            className="control-button"
                                            type="button"
                                            aria-pressed="false"
                                            disabled={!canEdit}
                                            onClick={() => {
                                                if (!canEdit) return;
                                                handleMenuClick();
                                                const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                                const curIdx = colOrder.indexOf(column?.insideElementId);
                                                moveColumnAbove(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                                if (curIdx > 0) {
                                                    dispatch(updateFocusedInsideElement(column?.elementId, column?.insideElementId, curIdx - 1, 'column'));
                                                }
                                            }}
                                        >
                                            <DraggableColumnsIcon />
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls"
                                        side="top"
                                        align="center"
                                        sideOffset={10}
                                    >
                                        <span>{t('t.ctx.tab_col.move_col_up')}</span>
                                        <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + <span className='text_shift'>{shortKeysMap?.SHIFT}</span> + Up</span>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            <div className="control-divider"></div>

                        </Fragment>
                    }
                    {/* description */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    ref={columnDescriptionBtnRef}
                                    className={`control-button ${colData?.description ? 'selected' : ''} ${clickedItem == 'column-description' ? 'clicked' : ''} `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('column-description');
                                    }}
                                >
                                    <ElementDescriptionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.description')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* duplicate */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control" style={{ marginRight: '-2px' }}>
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        const newColId = duplicateColumn(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                        if (newColId) {
                                            const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                            const curIdx = colOrder.indexOf(column?.insideElementId);
                                            dispatch(updateFocusedInsideElement(column?.elementId, newColId, curIdx >= 0 ? curIdx + 1 : colOrder.length, 'column'));
                                        }
                                    }}

                                >
                                    <CopyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.duplicate')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + C</span>
                                {/* <span className="tooltip-shortcut">C</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        const colOrder = elements[column?.elementId]?.columnsOrder || [];
                                        const curIdx = colOrder.indexOf(column?.insideElementId);
                                        deleteColumn(getDiagramDoc(), column?.elementId, column?.insideElementId);
                                        if (colOrder.length <= 1) {
                                            dispatch(updateFocusedInsideElement(column?.elementId, null, null, 'header'));
                                        } else if (curIdx < colOrder.length - 1) {
                                            dispatch(updateFocusedInsideElement(column?.elementId, colOrder[curIdx + 1], curIdx, 'column'));
                                        } else {
                                            dispatch(updateFocusedInsideElement(column?.elementId, colOrder[curIdx - 1], curIdx - 1, 'column'));
                                        }
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete')}</span>
                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + Del</span>
                                {/* <span className="tooltip-shortcut">Del</span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        )
    };

    function getConnectorText(connectorType) {
        switch (connectorType) {
            case 'empty':
                return t('t.ctx.line.connects.empty');
            case 'one':
                return t('t.ctx.line.connects.one_to');
            case 'many':
                return t('t.ctx.line.connects.many_to');
            case 'one-null':
                return t('t.ctx.line.connects.one_null_to');
            case 'many-null':
                return t('t.ctx.line.connects.many_null_to');
            default:
                return t('t.ctx.line.connects.empty');
        }
    }

    // line menu
    const getLineMenu = (focusedLine) => {
        if (!focusedLine) {
            return;
        }

        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };
        if (!focusedLine?.color) {
            colorCounter.none++;
        } else {
            colorCounter[focusedLine?.color]++;
        }

        const colorName = colorCounter.blue
            ? 'blue'
            : colorCounter.green
                ? 'green'
                : colorCounter.red
                    ? 'red'
                    : colorCounter.purple
                        ? 'purple'
                        : colorCounter.violet
                            ? 'violet'
                            : null;

        // 'fromElement' is in left, 'toElement' is in right  ==> true
        // 'fromElement' is in right, 'toElement' is in left  ==> false
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in top, 'toElement' is in bottom  ==> true
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in bottom, 'toElement' is in top  ==> false
        const fromEl = elements[(focusedLine?.sourceElementId ?? focusedLine?.fromElementId)];
        const toEl = elements[(focusedLine?.targetElementId ?? focusedLine?.toElementId)];
        const fromElementX = fromEl?.x ?? 0;
        const fromElementY = fromEl?.y ?? 0;
        const toElementX = toEl?.x ?? 0;
        const toElementY = toEl?.y ?? 0;
        const isLeftRightElements =
            fromElementX < toElementX
                ? true
                : fromElementX > toElementX
                    ? false
                    : fromElementY <= toElementY
                        ? true
                        : false;

        const leftRelationType = isLeftRightElements
            ? (focusedLine?.sourceCardinality ?? focusedLine?.fromRelationType)
            : (focusedLine?.targetCardinality ?? focusedLine?.toRelationType);
        const rightRelationType = isLeftRightElements
            ? (focusedLine?.targetCardinality ?? focusedLine?.toRelationType)
            : (focusedLine?.sourceCardinality ?? focusedLine?.fromRelationType);

        return (
            <div
                className={`menu`}
            // style={{ top: '14px', position: 'absolute', left: '37px' }}
            >
                <div className="controls-group">
                    {DragMenuPart}
                    <div className="control-divider"></div>

                    {/* left line connector */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType && leftRelationType !== 'empty' ? 'selected' : ''}
                                        ${clickedItem == 'left-line-connector' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('left-line-connector');
                                    }}
                                >
                                    {getLineConnector(leftRelationType, true)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{getConnectorText(leftRelationType)}</span>
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* swap line connectors */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        if (leftRelationType && rightRelationType) {
                                            setLineConnectorsReversed(getDiagramDoc(), focusedLine?.id, !(focusedLine?.isReversed ?? focusedLine?.connectorsReversed));
                                        }
                                    }}
                                >
                                    <SwapLinesIconNew />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.change_connects')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* right line connector */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType && rightRelationType !== 'empty' ? 'selected' : ''}
                                        ${clickedItem == 'right-line-connector' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('right-line-connector');
                                    }}
                                >
                                    {getLineConnector(rightRelationType, false)}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{getConnectorText(rightRelationType)}</span>
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>


                    {/* On Update Trigger */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button on-trigger-value ${clickedItem == 'on-update-trigger' ? 'clicked' : ''} ${focusedLine.onUpdateTrigger ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (isMoreBtnClicked) {
                                            setIsMoreBtnClicked(false);
                                        }
                                        handleMenuClick('on-update-trigger');
                                    }}
                                >
                                    ON UPDATE
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.on_update')}</span>
                                {/* <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 8
                                </span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* On Delete Trigger */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button on-trigger-value ${clickedItem == 'on-delete-trigger' ? 'clicked' : ''} ${focusedLine.onDeleteTrigger ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (isMoreBtnClicked) {
                                            setIsMoreBtnClicked(false);
                                        }
                                        handleMenuClick('on-delete-trigger');
                                    }}
                                >
                                    ON DELETE
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.on_delete')}</span>
                                {/* <span className="tooltip-shortcut">
                                    {shortKeysMap?.CTRL} + 8
                                </span> */}

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>


                    {/* Auto Resize Line */}

                    {/*
                    <div className="control">
                        <button
                            className='control-button'
                            type="button"
                            title="1-auto resize"
                            aria-label="Column Type"
                            aria-pressed="false"
                            onClick={() => {
                                handleMenuClick();
                            }}
                        >
                            RESIZE
                        </button>
                    </div>
                    */}

                    {/* color-picker */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorName ? 'selected' : ''}
                                        ${clickedItem == 'color-picker' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('color-picker');
                                    }}
                                >
                                    <BackgroundColorCircleIcon color={colorName || '#c8d9e0'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.color')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* border */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${['dashes', 'dots'].includes((focusedLine?.strokeStyle ?? focusedLine?.solidType)) ? 'selected' : ''}
                                        ${clickedItem == 'line-solid-type' ? 'clicked' : ''}
                                    `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('line-solid-type');
                                    }}
                                >
                                    <LineTypeIcon
                                        isDashes={(focusedLine?.strokeStyle ?? focusedLine?.solidType) === 'dashes'}
                                        isDots={(focusedLine?.strokeStyle ?? focusedLine?.solidType) === 'dots'}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.border')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* description */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${focusedLine?.description ? 'selected' : ''} ${clickedItem == 'line-description' ? 'clicked' : ''} `}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleMenuClick('line-description');
                                    }}
                                >
                                    <ElementDescriptionIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.description')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {/* delete */}
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className="control-button"
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleMenuClick();
                                        deleteLine(getDiagramDoc(), focusedLine?.id);
                                        setViewMode(null);
                                    }}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.delete')}</span>
                                <span className="tooltip-shortcut">Del</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    {VisibilityMenuPart}
                </div>
            </div>
        );
    };

    // enum menu
    const EnumMenu = () => {
        const [valueString, setValueString] = useState('');

        function parseEnumValues(input) {
            // Обрізаємо зайві пробіли з початку та кінця
            input = input.trim();

            // Якщо рядок огорнутий дужками, спробуємо їх прибрати.
            if ((input.startsWith('(') && input.endsWith(')')) ||
                (input.startsWith('(') && !input.endsWith(')')) ||
                (!input.startsWith('(') && input.endsWith(')'))) {
                // Спробуємо прибрати лише одну пару дужок, якщо вони збалансовані або хоча б початкова або кінцева дужка.
                if (input.startsWith('(')) {
                    input = input.substring(1).trim();
                }
                if (input.endsWith(')')) {
                    input = input.substring(0, input.length - 1).trim();
                }
            }

            const values = [];
            let current = '';
            let insideQuotes = false;
            let quoteChar = null; // може бути '\'' або '\"'

            for (let i = 0; i < input.length; i++) {
                const char = input[i];

                if (insideQuotes) {
                    // Ми знаходимося всередині лапок
                    if (char === quoteChar) {
                        // Зустріли закриваючу лапку
                        insideQuotes = false;
                        quoteChar = null;
                    } else {
                        // Продовжуємо накопичувати символи всередині лапок
                        current += char;
                    }
                } else {
                    // Знаходимося поза лапками
                    if ((char === '"' || char === "'")) {
                        // Починаємо режим лапок
                        insideQuotes = true;
                        quoteChar = char;
                    } else if (char === ',') {
                        // Кома поза лапками - роздільник значень
                        let trimmed = current.trim();
                        if (trimmed.length > 0) {
                            values.push(trimmed);
                        }
                        current = '';
                    } else {
                        // Звичайний символ
                        current += char;
                    }
                }
            }

            // Додаємо останній токен, якщо є
            let trimmed = current.trim();
            if (trimmed.length > 0) {
                values.push(trimmed);
            }

            return values;
        }

        function handleEnumOnChange(e) {
            setValueString(e.target.value);
        }

        function handleEnumOnSubmit() {
            if (!canEdit) return;
            const values = parseEnumValues(valueString);

            if (values?.length) {
                // enum
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnEnum(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, values);
                }
            } else {
                // not enum
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnEnum(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, null);
                }
            }

            handleSubMenuClick();
            setValueString('');
        }

        useEffect(() => {
            const enumValues = elements[focusedInsideElementRef.current?.elementId]?.columns?.[focusedInsideElementRef.current?.insideElementId]?.enumValues;
            if (enumValues) {
                setValueString(enumValues.join(', ') || '');
            }
            return () => { };
        }, []);



        if (!enumBtnRef.current || !menuRef.current) {
            return;
        }

        const contextMenuRect = menuRef.current?.getBoundingClientRect();
        const enumBtnRect = enumBtnRef?.current?.getBoundingClientRect();

        // const top = rect.bottom - contextMenuRect.bottom + contextMenuRect.height;
        const top = 51;
        const left = enumBtnRect.left - contextMenuRect.left - 327 / 2 + 25 + 34;

        return (
            <div
                className={`menu`}
                style={{ position: 'absolute', top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">

                    {/* <div className="control"> */}
                    <input
                        maxLength="1000"
                        ref={enumValueMenuInputRef}
                        tabIndex="2"
                        autoFocus={(window.innerWidth >= 1000)}
                        className='enum-input'
                        id="enum-field"
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        value={valueString}
                        placeholder={t('t.ctx.tab_col.enum_place')}
                        onChange={(e) => handleEnumOnChange(e)}
                    />
                    {/* </div> */}

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            ref={enumValueMenuButtonRef}
                            tabIndex="3"
                            className="control-button enum-click"
                            type="button"
                            // aria-pressed="false"
                            onClick={() => { handleEnumOnSubmit() }}
                        >
                            {/* <OkCheckIcon /> */}
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>

                    {/* <div className="control control-m5">
                        <button
                            className="control-button link-add"
                            type="button"
                            disabled={!canLink}
                            onClick={handleLinkApply}
                        >
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div> */}


                </div>
            </div>
        );
    };

    // default value menu
    const DefaultValueMenu = () => {
        const [valueString, setValueString] = useState('');

        function handleDefaultValueOnChange(e) {
            setValueString(e.target.value);
        }

        function handleDefaultValueOnSubmit() {
            if (!canEdit) return;
            if (valueString) {
                // default value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnDefaultValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, valueString);
                }
            } else {
                // not default value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnDefaultValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, null);
                }
            }

            handleSubMenuClick();
            setValueString('');
        }

        useEffect(() => {
            const defaultValue = elements[focusedInsideElementRef.current?.elementId]?.columns?.[focusedInsideElementRef.current?.insideElementId]?.defaultValue;
            if (defaultValue) {
                setValueString(defaultValue);
            }
            return () => { };
        }, []);




        if (!defaultValueBtnRef.current || !menuRef.current) {
            return;
        }

        const contextMenuRect = menuRef.current?.getBoundingClientRect();
        const defaultValueBtnRect = defaultValueBtnRef?.current?.getBoundingClientRect();

        const top = 51;
        const left = defaultValueBtnRect.left - contextMenuRect.left - 327 / 2 + 25;

        return (
            <div
                className={`menu`}
                style={{ position: 'absolute', top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">

                    {/* <div className="control"> */}
                    <input
                        maxLength="1000"
                        ref={defaultValueMenuInputRef}
                        tabIndex="2"
                        autoFocus={(window.innerWidth >= 1000)}
                        className='enum-input'
                        id="default-value-field"
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        value={valueString}
                        placeholder={t('t.ctx.tab_col.default_value_place')}
                        onChange={(e) => handleDefaultValueOnChange(e)}
                    />
                    {/* </div> */}

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            ref={defaultValueMenuButtonRef}
                            tabIndex="3"
                            className="control-button enum-click"
                            type="button"
                            onClick={() => { handleDefaultValueOnSubmit() }}
                        >
                            {/* <OkCheckIcon /> */}
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>

                </div>
            </div>
        );
    };



    // generated value menu
    const GeneratedValueMenu = () => {
        // const textAreaRef = useRef(null);

        const [valueString, setValueString] = useState('');

        function handleGeneratedValueOnChange(e) {
            setValueString(e.target.value);
        }

        function handleGeneratedValueOnSubmit() {
            if (!canEdit) return;
            if (valueString) {
                // generated value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnGeneratedValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, valueString);
                }
            } else {
                // not generated value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnGeneratedValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, null);
                }
            }

            handleSubMenuClick();
            setValueString('');
        }



        // Функція для автоматичного коригування висоти
        const adjustHeight = () => {
            if (generatedValueMenuInputRef.current) {
                // Скидаємо висоту перед тим, як визначити нову
                generatedValueMenuInputRef.current.style.height = 'auto';
                // Встановлюємо висоту на основі контенту, але не більше максимального розміру
                generatedValueMenuInputRef.current.style.height = `${generatedValueMenuInputRef.current.scrollHeight + 4}px`;
            }
        };
        useEffect(() => {
            adjustHeight();
        }, [valueString]);


        useEffect(() => {
            const generatedValue = elements[focusedInsideElementRef.current?.elementId]?.columns?.[focusedInsideElementRef.current?.insideElementId]?.generatedValue;
            if (generatedValue) {
                setValueString(generatedValue);
            }
            return () => { };
        }, []);


        if (!generatedValueBtnRef.current || !menuRef.current) {
            return;
        }

        const contextMenuRect = menuRef.current?.getBoundingClientRect();
        const generatedValueBtnRect = generatedValueBtnRef?.current?.getBoundingClientRect();

        const top = 51;
        const left = generatedValueBtnRect.left - contextMenuRect.left - 327 / 2 + 25;

        return (
            <div
                className={`menu`}
                style={{ position: 'absolute', top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">

                    <textarea
                        maxLength="1000"
                        ref={generatedValueMenuInputRef}
                        // ref={textAreaRef}
                        tabIndex="2"
                        autoFocus={(window.innerWidth >= 1000)}
                        id="generated-field"
                        className='description-textarea'
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        value={valueString}
                        placeholder={t('t.ctx.tab_col.generated_value_place')}
                        onChange={(e) => handleGeneratedValueOnChange(e)}
                    />

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            ref={generatedValueMenuButtonRef}
                            tabIndex="3"
                            className="control-button enum-click"
                            type="button"
                            onClick={() => { handleGeneratedValueOnSubmit() }}
                        >
                            {/* <OkCheckIcon /> */}
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>

                </div>
            </div>
        );
    };

    // check value menu
    const CheckValueMenu = () => {
        // const textAreaRef = useRef(null);

        const [valueString, setValueString] = useState('');

        function handleCheckValueOnChange(e) {
            setValueString(e.target.value);
        }

        function handleCheckValueOnSubmit() {
            if (!canEdit) return;
            if (valueString) {
                // check value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnCheckValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, valueString);
                }
            } else {
                // not check value
                if (focusedInsideElementRef.current?.elementId && focusedInsideElementRef.current?.insideElementId) {
                    setColumnCheckValue(getDiagramDoc(), focusedInsideElementRef.current?.elementId, focusedInsideElementRef.current?.insideElementId, null);
                }
            }

            handleSubMenuClick();
            setValueString('');
        }



        // Функція для автоматичного коригування висоти
        const adjustHeight = () => {
            if (checkValueMenuInputRef.current) {
                // Скидаємо висоту перед тим, як визначити нову
                checkValueMenuInputRef.current.style.height = 'auto';
                // Встановлюємо висоту на основі контенту, але не більше максимального розміру
                checkValueMenuInputRef.current.style.height = `${checkValueMenuInputRef.current.scrollHeight + 4}px`;
            }
        };
        useEffect(() => {
            adjustHeight();
        }, [valueString]);


        useEffect(() => {
            const checkValue = elements[focusedInsideElementRef.current?.elementId]?.columns?.[focusedInsideElementRef.current?.insideElementId]?.checkValue;
            if (checkValue) {
                setValueString(checkValue);
            }
            return () => { };
        }, []);


        if (!checkValueBtnRef.current || !menuRef.current) {
            return;
        }

        const contextMenuRect = menuRef.current?.getBoundingClientRect();
        const checkValueBtnRect = checkValueBtnRef?.current?.getBoundingClientRect();

        const top = 51;
        const left = checkValueBtnRect.left - contextMenuRect.left - 327 / 2 + 25;

        return (
            <div
                className={`menu`}
                style={{ position: 'absolute', top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">

                    <textarea
                        maxLength="1000"
                        ref={checkValueMenuInputRef}
                        // ref={textAreaRef}
                        tabIndex="2"
                        autoFocus={(window.innerWidth >= 1000)}
                        id="check-field"
                        className='description-textarea'
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        value={valueString}
                        placeholder={t('t.ctx.tab_col.check_place')}
                        onChange={(e) => handleCheckValueOnChange(e)}
                    />

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            ref={checkValueMenuButtonRef}
                            tabIndex="3"
                            className="control-button enum-click"
                            type="button"
                            onClick={() => { handleCheckValueOnSubmit() }}
                        >
                            {/* <OkCheckIcon /> */}
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>

                </div>
            </div>
        );
    };


    // description menu
    const DescriptionMenu = (props) => {
        // const textAreaRef = useRef(null);

        const [valueString, setValueString] = useState('');

        let positionTop = 0;
        let positionLeft = 0;

        switch (props.type) {
            case 'note': {
                positionTop = 51;
                positionLeft = 303;
                break;
            }
            case 'pen': {
                positionTop = 51;
                // positionLeft = 20;
                positionLeft = 6;
                break;
            }
            case 'table': {
                positionTop = 51;
                // positionLeft = 120;
                // positionLeft = 5 - 30;
                positionLeft = 7;
                break;
            }
            case 'column': {
                positionTop = 51;
                positionLeft = 362;
                positionLeft = 434;
                break;
            }
            case 'area': {
                positionTop = 51;
                // positionLeft = 119 - 40;
                positionLeft = 108;
                break;
            }
            case 'line': {
                positionTop = 51;
                // positionLeft = 330 - 30;
                positionLeft = 325;
                break;
            }
            default:
                break;
        }

        function handleDescriptionOnChange(e) {
            setValueString(e.target.value);
        }

        function handleDescriptionOnSubmit() {
            if (!canEdit) return;
            const value = valueString || null;

            switch (props.type) {
                case 'pen':
                case 'note':
                case 'table': {
                    const keys = Object.keys(focusedElementsById);
                    if (!keys.length) {
                        return;
                    }
                    const element = focusedElementsById[keys[0]];
                    setElementDescription(getDiagramDoc(), element.id, value);
                    break;
                }
                case 'column': {
                    const column = focusedInsideElementRef.current;
                    if (column?.elementId && column?.insideElementId) {
                        setColumnDescription(getDiagramDoc(), column?.elementId, column?.insideElementId, value);
                    }
                    break;
                }
                case 'area': {
                    if (!focusedArea) {
                        return;
                    }
                    setAreaDescription(getDiagramDoc(), focusedArea.id, value);
                    break;
                }
                case 'line': {
                    if (!focusedLine) {
                        return;
                    }
                    setLineDescription(getDiagramDoc(), focusedLine.id, value);
                    break;
                }
                default:
                    break;
            }

            handleSubMenuClick();
            setValueString('');
        }

        useEffect(() => {
            let descriptionValue = '';

            switch (props.type) {
                case 'note':
                case 'pen':
                case 'table': {
                    const keys = Object.keys(focusedElementsById);
                    if (!keys.length) {
                        return;
                    }
                    const element = focusedElementsById[keys[0]];
                    descriptionValue = element?.description || '';
                    break;
                }
                case 'column': {
                    const column = focusedInsideElementRef.current;
                    descriptionValue = elements[column?.elementId]?.columns?.[column?.insideElementId]?.description || '';
                    break;
                }
                case 'area': {
                    if (!focusedArea) {
                        return;
                    }
                    descriptionValue = focusedArea?.description || '';
                    break;
                }
                case 'line': {
                    if (!focusedLine) {
                        return;
                    }
                    descriptionValue = focusedLine?.description || '';
                    break;
                }
                default:
                    break;
            }

            if (descriptionValue) {
                setValueString(descriptionValue);
            }

            return () => { };
        }, []);


        // Функція для автоматичного коригування висоти
        const adjustHeight = () => {
            if (descriptionMenuInputRef.current) {
                // Скидаємо висоту перед тим, як визначити нову
                descriptionMenuInputRef.current.style.height = 'auto';
                // Встановлюємо висоту на основі контенту, але не більше максимального розміру
                descriptionMenuInputRef.current.style.height = `${descriptionMenuInputRef.current.scrollHeight + 4}px`;
            }
        };
        useEffect(() => {
            adjustHeight();
        }, [valueString]);




        // if (props.type == 'column') {
        //     if (!columnDescriptionBtnRef.current || !menuRef.current) {
        //         return;
        //     }

        //     const contextMenuRect = menuRef.current?.getBoundingClientRect();
        //     const columnDescriptionBtnRect = columnDescriptionBtnRef?.current?.getBoundingClientRect();

        //     positionTop = 51;
        //     positionLeft = columnDescriptionBtnRect.left - contextMenuRect.left - 324 / 2 + 25 - 30;
        // }

        return (
            <div
                className={`menu`}
                // style={{ position: 'absolute', top: `${top}px`, left: `${left}px` }}
                style={{ position: 'absolute', top: `${positionTop}px`, left: `${positionLeft}px` }}
            >
                <div className="controls-group">

                    {/* <div className="control"> */}
                    <textarea
                        maxLength="1000"
                        ref={descriptionMenuInputRef}
                        // ref={textAreaRef}
                        tabIndex="2"
                        autoFocus={(window.innerWidth >= 1000)}
                        id="description-field"
                        className='description-textarea'
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        value={valueString}
                        // placeholder={`${props.type === 'pen' ? 'pen-segment' : props.type} description`}
                        // placeholder={t('t.ctx.base.description_place', { type: props.type === 'pen' ? 'pen-segment' : props.type })}
                        placeholder={t(`t.ctx.base.description_${props.type}_place`)}
                        onChange={(e) => handleDescriptionOnChange(e)}
                    />
                    {/* </div> */}

                    <div className="control-divider divider-m1"></div>

                    <div className="control control-m5">
                        <button
                            ref={descriptionMenuButtonRef}
                            tabIndex="3"
                            className="control-button enum-click"
                            type="button"
                            onClick={() => { handleDescriptionOnSubmit() }}
                        >
                            {/* <OkCheckIcon /> */}
                            {t('t.ctx.note.link_apply')}
                        </button>
                    </div>

                </div>
            </div>
        );
    };

    // key menu
    const getKeyMenu = () => {
        if (!focusedInsideElementRef.current) {
            return;
        }

        const column = focusedInsideElementRef.current;

        if (!column?.elementId || !column?.insideElementId) {
            return;
        }

        const isPK = elements[column?.elementId]?.columns?.[column?.insideElementId]?.isPK;
        const isFK = elements[column?.elementId]?.columns?.[column?.insideElementId]?.isFK;

        return (
            <div
                className={`menu key-menu`}
                style={{ top: '51px', position: 'absolute', left: `${133 - 53 - 9}px` }}
            >
                <div className="controls-group">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isPK || isFK) {
                                            setColumnKey(getDiagramDoc(), column?.elementId, column?.insideElementId, null);
                                        }
                                    }}
                                    style={{ fontSize: '24px', fontWeight: 700 }}
                                >
                                    <CircleNoneIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.keys.none')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isPK ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (!isPK) {
                                            setColumnKey(getDiagramDoc(), column?.elementId, column?.insideElementId, 'pk');
                                        }
                                    }}
                                >
                                    <KeyIcon isPK={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.keys.pk')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isFK ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (!isFK) {
                                            setColumnKey(getDiagramDoc(), column?.elementId, column?.insideElementId, 'fk');
                                        }
                                    }}
                                >
                                    <KeyIcon isFK={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.keys.fk')}</span>
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div >
        )
    };
    // index menu
    const getIndexMenu = () => {
        if (!focusedInsideElementRef.current) {
            return;
        }

        const column = focusedInsideElementRef.current;

        if (!column?.elementId || !column?.insideElementId) {
            return;
        }

        const indexType = elements[column?.elementId]?.columns?.[column?.insideElementId]?.indexType;

        return (
            <div
                className={`menu index-menu`}
                style={{ top: '51px', position: 'absolute', left: `${172 - 99 + 26 + 12}px` }}
            >
                <div className="controls-group">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (indexType) {
                                            setColumnIndex(getDiagramDoc(), column?.elementId, column?.insideElementId, null);
                                        }
                                    }}
                                    style={{ fontSize: '24px', fontWeight: 700, height: '37px' }}
                                >
                                    <CircleNoneIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.indexes.none')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button index ${indexType === 'regular' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (indexType !== 'regular') {
                                            setColumnIndex(getDiagramDoc(), column?.elementId, column?.insideElementId, 'regular');
                                        }
                                    }}
                                >
                                    <svg
                                        className={`context-menu-column-index menu-item regular ${indexType === 'regular' ? 'selected' : ''}`}
                                    >
                                        <polygon
                                            points={`${rombRadius + 1},1 ${rombRadius * 2 + 1},${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1} 1,${rombRadius + 1}`}
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.indexes.regular')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button index ${indexType === 'unique' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (indexType !== 'unique') {
                                            setColumnIndex(getDiagramDoc(), column?.elementId, column?.insideElementId, 'unique');
                                        }
                                    }}
                                >
                                    <svg
                                        className={`context-menu-column-index menu-item unique`}
                                    >
                                        <polygon
                                            points={`${rombRadius + 1},1 ${rombRadius * 2 + 1},${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1} 1,${rombRadius + 1}`}
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.indexes.unique')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    { /* Composite index*/}
                    {/* <Tooltip.Root delayDuration={600} disableHoverableContent>
                    <Tooltip.Trigger asChild>
                        <div className="control">
                            <button
                                className={`control-button ${indexType === 'composite' ? 'selected' : '' }`}
                                type="button"
                                aria-pressed="false"
                                onClick={() => {
                                    handleSubMenuClick();
                                    if (indexType !== 'composite') {
                                        setColumnIndex(getDiagramDoc(), column?.elementId, column?.insideElementId, 'composite');
                                    }
                                }}
                            >
                                <svg
                                    className={`context-menu-column-index composite`}
                                >
                                    <polygon
                                        points={`${rombRadius + 1},1 ${rombRadius * 2 + 1 },${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1 } 1,${rombRadius + 1}`}
                                    />
                                </svg>
                            </button>
                        </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content
                            className="tooltip-content tooltip-controls"
                            side="bottom"
                            align="center"
                            sideOffset={10}
                        >
                            <span>Composite</span>

                            <Tooltip.Arrow className="tooltip-arrow" />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root> */}

                    {/* <div className="control-divider"></div> */}

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button index ${indexType === 'fullText' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    disabled={!canEdit}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (indexType !== 'fullText') {
                                            setColumnIndex(getDiagramDoc(), column?.elementId, column?.insideElementId, 'fullText');
                                        }
                                    }}
                                >
                                    <svg
                                        className={`context-menu-column-index menu-item fullText`}
                                    >
                                        <polygon
                                            points={`${rombRadius + 1},1 ${rombRadius * 2 + 1},${rombRadius + 1} ${rombRadius + 1},${rombRadius * 2 + 1} 1,${rombRadius + 1}`}
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.tab_col.indexes.full_text')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        )
    };

    // left line connector menu
    const getleftLineConnectorMenu = (focusedLine) => {
        if (!focusedLine) {
            return;
        }

        // 'fromElement' is in left, 'toElement' is in right  ==> true
        // 'fromElement' is in right, 'toElement' is in left  ==> false
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in top, 'toElement' is in bottom  ==> true
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in bottom, 'toElement' is in top  ==> false
        const fromElL = elements[(focusedLine?.sourceElementId ?? focusedLine?.fromElementId)];
        const toElL = elements[(focusedLine?.targetElementId ?? focusedLine?.toElementId)];
        const isLeftRightElements =
            (fromElL?.x ?? 0) < (toElL?.x ?? 0)
                ? true
                : (fromElL?.x ?? 0) > (toElL?.x ?? 0)
                    ? false
                    : (fromElL?.y ?? 0) <= (toElL?.y ?? 0)
                        ? true
                        : false;

        const leftRelationType = isLeftRightElements
            ? (focusedLine?.sourceCardinality ?? focusedLine?.fromRelationType)
            : (focusedLine?.targetCardinality ?? focusedLine?.toRelationType);
        const direction = isLeftRightElements ? 'from' : 'to';

        return (
            <div
                className={`menu`}
                // style={{ top: '68px', position: 'absolute', left: '71px' }}
                style={{ top: '51px', position: 'absolute', left: '31px' }}
            >
                <div className="controls-group vertical vertical-small">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType === 'empty' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (leftRelationType !== 'empty') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'empty' });
                                        }
                                    }}
                                >
                                    <LineConnectorEmptyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.empty')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType === 'one' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (leftRelationType !== 'one') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'one' });
                                        }
                                    }}
                                >
                                    <LineConnectorOneIcon isLeft={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.one_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType === 'many' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();

                                        if (leftRelationType !== 'many') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'many' });
                                        }
                                    }}
                                >
                                    <LineConnectorManyIcon isLeft={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.many_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType === 'one-null' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (leftRelationType !== 'one-null') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'one-null' });
                                        }
                                    }}
                                >
                                    <LineConnectorOneNullIcon isLeft={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.one_null_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${leftRelationType === 'many-null' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (leftRelationType !== 'many-null') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'many-null' });
                                        }
                                    }}
                                >
                                    <LineConnectorManyNullIcon isLeft={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.many_null_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // right line connector menu
    const getRightLineConnectorMenu = (focusedLine) => {
        if (!focusedLine) {
            return;
        }

        // 'fromElement' is in left, 'toElement' is in right  ==> true
        // 'fromElement' is in right, 'toElement' is in left  ==> false
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in top, 'toElement' is in bottom  ==> true
        // 'fromElement' and 'toElement' equal by 'x', and 'fromElement' is in bottom, 'toElement' is in top  ==> false
        const fromElR = elements[(focusedLine?.sourceElementId ?? focusedLine?.fromElementId)];
        const toElR = elements[(focusedLine?.targetElementId ?? focusedLine?.toElementId)];
        const isLeftRightElements =
            (fromElR?.x ?? 0) < (toElR?.x ?? 0)
                ? true
                : (fromElR?.x ?? 0) > (toElR?.x ?? 0)
                    ? false
                    : (fromElR?.y ?? 0) <= (toElR?.y ?? 0)
                        ? true
                        : false;

        const rightRelationType = isLeftRightElements
            ? (focusedLine?.targetCardinality ?? focusedLine?.toRelationType)
            : (focusedLine?.sourceCardinality ?? focusedLine?.fromRelationType);
        const direction = isLeftRightElements ? 'to' : 'from';

        return (
            <div
                className={`menu`}
                // style={{ top: '68px', position: 'absolute', left: `${174}px` }}
                style={{ top: '51px', position: 'absolute', left: `${127}px` }}
            >
                <div className="controls-group vertical vertical-small">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType === 'empty' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                        if (rightRelationType !== 'empty') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'empty' });
                                        }
                                    }}
                                >
                                    <LineConnectorEmptyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.empty')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType === 'one' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                        if (rightRelationType !== 'one') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'one' });
                                        }
                                    }}
                                >
                                    <LineConnectorOneIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.one_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType === 'many' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                        if (rightRelationType !== 'many') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'many' });
                                        }
                                    }}
                                >
                                    <LineConnectorManyIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.many_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType === 'one-null' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                        if (rightRelationType !== 'one-null') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'one-null' });
                                        }
                                    }}
                                >
                                    <LineConnectorOneNullIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.one_null_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${rightRelationType === 'many-null' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                        if (rightRelationType !== 'many-null') {
                                            setLineConnector(getDiagramDoc(), focusedLine?.id, { direction, relationType: 'many-null' });
                                        }
                                    }}
                                >
                                    <LineConnectorManyNullIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.line.connects.many_null_to')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // line type menu
    const getLineSolidTypeMenu = (focusedLine) => {
        if (!focusedLine) {
            return;
        }

        return (
            <div
                className={`menu`}
                // style={{ top: '68px', position: 'absolute', left: `${236}px` }}
                style={{ top: '51px', position: 'absolute', left: `${418}px` }}
            >
                <div className="controls-group vertical vertical-small">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${(!(focusedLine?.strokeStyle ?? focusedLine?.solidType) || (focusedLine?.strokeStyle ?? focusedLine?.solidType) === 'solid') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        setLineStrokeStyle(getDiagramDoc(), focusedLine?.id, 'solid');
                                    }}
                                >
                                    <LineTypeIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.solid')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${(focusedLine?.strokeStyle ?? focusedLine?.solidType) === 'dashes' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        setLineStrokeStyle(getDiagramDoc(), focusedLine?.id, 'dashes');
                                    }}
                                >
                                    <LineTypeIcon isDashes={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.dashed')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector ${(focusedLine?.strokeStyle ?? focusedLine?.solidType) === 'dots' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        setLineStrokeStyle(getDiagramDoc(), focusedLine?.id, 'dots');
                                    }}
                                >
                                    <LineTypeIcon isDots={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.dotted')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // element border menu
    const getElementBorderMenu = (focusedElements, focusedArea) => {
        const areaCount = focusedArea ? 1 : 0;
        const elementsCount = Object.keys(focusedElements).length;
        let itemsCount = 0;

        if (!elementsCount && !focusedArea) {
            return;
        }

        let borderCounter = { none: 0, solid: 0, dashes: 0, dots: 0 };
        let positionTop = 0;
        let positionLeft = 0;

        // 1. elements are focused
        if (elementsCount) {
            itemsCount = elementsCount;

            // 1.1 elements border
            for (let key in focusedElements) {
                const element = focusedElements[key];

                if (!element?.border) {
                    borderCounter.none++;
                } else {
                    borderCounter[element?.border]++;
                }
            }

            // 1.2. elements menu position
            if (elementsCount > 1) {
                positionTop = 51;
                // positionLeft = 188;
                // positionLeft = 229;
                // positionLeft = 219;
                positionLeft = 200;
            } else {
                const elementKey = Object.keys(focusedElements)[0];
                const element = focusedElements[elementKey];

                if (element.type === 'table') {
                    positionTop = 51;
                    positionLeft = 73; // 79;
                } else if (element.type === 'note') {
                    positionTop = 51;
                    positionLeft = 375;
                }
            }

            // 2. area is focused
        } else {
            itemsCount = areaCount;

            // 2.1. area border
            if (!focusedArea?.border) {
                borderCounter.solid++;
            } else {
                borderCounter[focusedArea?.border]++;
            }

            // 2.2. area position
            positionTop = 51;
            // positionLeft = 187;
            positionLeft = 173;
        }

        return (
            <div
                className={`menu element-borders-menu`}
                style={{
                    top: `${positionTop}px`,
                    position: 'absolute',
                    left: subMenuLeftPx(positionLeft, -4),
                }}
            >
                <div className="controls-group vertical vertical-small">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();

                                        if (elementsCount) {
                                            setElementBorder(getDiagramDoc(), focusedElementsByIdNoPens, null);
                                        } else {
                                            setAreaBorder(getDiagramDoc(), focusedAreas[0], null);
                                        }
                                    }}
                                >
                                    <CircleNoneIcon isBorder={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.none')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border ${borderCounter.solid == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (elementsCount) {
                                            setElementBorder(getDiagramDoc(), focusedElementsByIdNoPens, 'solid');
                                        } else {
                                            setAreaBorder(getDiagramDoc(), focusedAreas[0], 'solid');
                                        }
                                    }}
                                >
                                    <ElementBorderIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.solid')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border ${borderCounter.dashes == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (elementsCount) {
                                            setElementBorder(getDiagramDoc(), focusedElementsByIdNoPens, 'dashes');
                                        } else {
                                            setAreaBorder(getDiagramDoc(), focusedAreas[0], 'dashes');
                                        }
                                    }}
                                >
                                    <ElementBorderIcon isDashes={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.dashed')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button line-connector border ${borderCounter.dots == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (elementsCount) {
                                            setElementBorder(getDiagramDoc(), focusedElementsByIdNoPens, 'dots');
                                        } else {
                                            setAreaBorder(getDiagramDoc(), focusedAreas[0], 'dots');
                                        }
                                    }}
                                >
                                    <ElementBorderIcon isDots={true} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="right"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.borders.dotted')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // color picker menu
    // pen colour menu — the same three colours offered when creating a new pen
    const getFilterSelectionMenu = (focusedElements) => {
        const allIds = Object.keys(focusedElements);
        if (!allIds.length) return;
        const counts = filterSelectionCounts(focusedElements);
        const types = Object.keys(counts);

        // "All elements" first (only useful when the selection is mixed), then one row
        // per type. Selecting a row narrows the selection to just those elements.
        const rows = [];
        // if (types.length > 1) {
        //     rows.push({
        //         key: 'all',
        //         label: `${allIds.length} Element${allIds.length === 1 ? '' : 's'}`,
        //         ids: allIds,
        //     });
        // }
        const orderedTypes = [
            ...FILTER_TYPE_ORDER.filter((t) => counts[t]),
            ...types.filter((t) => !FILTER_TYPE_ORDER.includes(t)),
        ];
        for (const type of orderedTypes) {
            rows.push({
                key: type,
                type,
                label: filterSelectionType(type, counts[type].count),
                ids: counts[type].ids,
            });
        }

        return (
            <div
                className={`menu`}
                style={{ top: '51px', position: 'absolute', left: '31px' }}
            >
                <div className="controls-group vertical filter-selection-menu">
                    {rows.map(({ key, label, type, count, ids }) => (
                        <div className="control control-filter-selection" key={key}>
                            <button
                                className="control-button filter-selection filter-selection-option"
                                type="button"
                                aria-pressed="false"
                                onClick={() => {
                                    handleSubMenuClick();
                                    dispatch(updateFocusedIds(ids));
                                }}
                            >
                                <div className='filter-selection-option-left-part'>
                                    {type === 'table' ? <svg fill="#f7f8f8" width="17px" height="31px" viewBox="0 0 32 32" style={{ marginRight: '8px', marginLeft: '-2px', transition: 'all 0.15s ease' }}><g><path d="M 27 3 H 5 A 2 2 0 0 0 3 5 V 27 a 2 2 0 0 0 2 2 H 27 a 2 2 0 0 0 2 -2 V 5 A 2 2 0 0 0 27 3 Z m 0 4 V 13 H 5 V 7 Z M 17 15 H 27 v 5 H 17 Z m -2 5 H 5 V 15 H 15 Z M 5 22 H 15 v 5 H 5 Z m 12 5 V 22 H 27 v 5 Z" strokeWidth="0.2px" stroke="white"></path></g></svg> : ''}
                                    {/* {type === 'note' ? <svg width="17px" height="30px" viewBox="-1.5 -0.5 33 25" fill="#f7f8f8" stroke="#f7f8f8" strokeWidth="1.5" style={{ transform: 'rotate(180deg)', marginLeft: '-2px', marginTop: '2px', marginRight: '8px' }}><g><path d="M 11.5 12 M 27.5 6 V 19.5858 a 1.4903 1.4903 0 0 1 -0.4394 1.0607 l -6.4141 6.4142 A 1.4902 1.4902 0 0 1 19.5858 27.5 H 6 a 1.5017 1.5017 0 0 1 -1.5 -1.5 V 1 A 1.5017 1.5017 0 0 1 6 -0.5 H 26 A 1.5017 1.5017 0 0 1 27.5 1 Z M 6 26.5 H 19.5 V 20 a 0.5 0.5 0 0 1 0.5 -0.5 h 6.5 V 1 a 0.5006 0.5006 0 0 0 -0.5 -0.5 H 6 a 0.5006 0.5006 0 0 0 -0.5 0.5 V 26 A 0.5006 0.5006 0 0 0 6 26.5 Z m 19.7939 -6.001 H 20.5 v 5.2938 Z"></path></g></svg> : ''} */}
                                    {type === 'note' ? <svg width="19px" height="20px" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '-3px', marginRight: '6px', minWidth: '19px' }}><path d="M5 4h14a1 1 0 0 1 1 1v9l-6 6H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" stroke="#e1e1e1" strokeWidth="1.9" strokeLinejoin="round"></path><path d="M14 20v-5a1 1 0 0 1 1-1h5" stroke="#e1e1e1" strokeWidth="1.9" strokeLinejoin="round"></path></svg> : ''}
                                    {type === 'area' ? <svg width="18px" height="32px" viewBox="0 0 256 256" fill="none" strokeWidth="10" stroke="#f7f8f8" style={{ marginLeft: '-2px', marginRight: '8px' }}><g> <path d="M148,40a4.0002,4.0002,0,0,1-4,4H112a4,4,0,0,1,0-8h32A4.0002,4.0002,0,0,1,148,40Zm-4,172H112a4,4,0,0,0,0,8h32a4,4,0,0,0,0-8ZM208,36H184a4,4,0,0,0,0,8h24a4.00458,4.00458,0,0,1,4,4V71.99951a4,4,0,0,0,8,0V48A12.01375,12.01375,0,0,0,208,36Zm8,71.99951a4.0002,4.0002,0,0,0-4,4v32a4,4,0,0,0,8,0v-32A4.0002,4.0002,0,0,0,216,107.99951Zm0,72a4.0002,4.0002,0,0,0-4,4V208a4.00458,4.00458,0,0,1-4,4H184a4,4,0,0,0,0,8h24a12.01375,12.01375,0,0,0,12-12V183.99951A4.0002,4.0002,0,0,0,216,179.99951Zm-176-32a4.0002,4.0002,0,0,0,4-4v-32a4,4,0,1,0-8,0v32A4.0002,4.0002,0,0,0,40,147.99951ZM72,212H48a4.00458,4.00458,0,0,1-4-4V183.99951a4,4,0,0,0-8,0V208a12.01375,12.01375,0,0,0,12,12H72a4,4,0,0,0,0-8ZM72,36H48A12.01375,12.01375,0,0,0,36,48V71.99951a4,4,0,0,0,8,0V48a4.00458,4.00458,0,0,1,4-4H72a4,4,0,0,0,0-8Z" strokeWidth="13px"></path> </g></svg> : ''}
                                    {type === 'pen-segment' ? <svg fill="#ffffff" width="15px" height="30px" viewBox="0 0 36 36" style={{ marginRight: '7px', marginLeft: '0px', strokeWidth: '0.2px', fill: '#f7f8f8', stroke: '#f7f8f8' }}><g><path d="M33.87,8.32,28,2.42a2.07,2.07,0,0,0-2.92,0L4.27,23.2l-1.9,8.2a2.06,2.06,0,0,0,2,2.5,2.14,2.14,0,0,0,.43,0L13.09,32,33.87,11.24A2.07,2.07,0,0,0,33.87,8.32ZM12.09,30.2,4.32,31.83l1.77-7.62L21.66,8.7l6,6ZM29,13.25l-6-6,3.48-3.46,5.9,6Z" stroke="white" strokeWidth="1.2px"></path></g></svg> : ''}
                                    {label}
                                </div>
                                <div className='filter-selection-option-right-part'>{ids.length}</div>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getPenColorPickerMenu = (focusedElements) => {
        const elementsCount = Object.keys(focusedElements).length;
        if (!elementsCount) return;
        const segment = focusedElements[Object.keys(focusedElements)[0]];
        const penColors = [
            { value: 'yellow', display: '#ffc65e', label: 'Yellow' },
            { value: 'red', display: 'red', label: 'Red' },
            { value: 'lightgreen', display: 'lightgreen', label: 'Green' },
        ];
        // Position under the colour trigger button (measured); the per-count values are
        // only fallbacks for when the trigger can't be measured.
        const left = subMenuLeftPx(elementsCount > 1 ? 105 : -10, elementsCount > 1 ? -45 : -45);
        return (
            <div
                className={`menu`}
                style={{ top: '51px', position: 'absolute', left }}
            >
                <div className="controls-group color-picker">
                    {penColors.map(({ value, display, label }) => (
                        <Tooltip.Root key={value} delayDuration={600} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control">
                                    <button
                                        className={`control-button ${segment?.color === value ? 'selected' : ''}`}
                                        type="button"
                                        aria-pressed="false"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            if (!canEdit) return;
                                            handleSubMenuClick();
                                            setElementColor(getDiagramDoc(), focusedElements, value);
                                        }}
                                    >
                                        <BackgroundColorCircleIcon isItem={true} color={display} />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls wo-shortcut"
                                    side="top"
                                    align="center"
                                    sideOffset={10}
                                >
                                    <span>{label}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    ))}
                </div>
            </div>
        );
    };

    const getColorPickerMenu = (focusedElements, focusedLine, focusedArea) => {
        const elementsCount = Object.keys(focusedElements).length;
        if (!elementsCount && !focusedLine && !focusedArea) {
            return;
        }

        let colorCounter = {
            none: 0,
            blue: 0,
            green: 0,
            red: 0,
            purple: 0,
            violet: 0,
        };

        let isElementsFocused = false;

        if (elementsCount) {
            isElementsFocused = true;
            for (let key in focusedElements) {
                const element = focusedElements[key];

                if (!element?.color) {
                    colorCounter.none++;
                } else {
                    colorCounter[element?.color]++;
                }
            }
        } else if (focusedLine) {
            if (!focusedLine?.color) {
                colorCounter.none++;
            } else {
                colorCounter[focusedLine?.color]++;
            }
        } else {
            if (!focusedArea?.color) {
                colorCounter.none++;
            } else {
                colorCounter[focusedArea?.color]++;
            }
        }

        const itemsCount = isElementsFocused ? elementsCount : 1;

        let positionTop = 0;
        let positionLeft = 0;

        if (isElementsFocused && elementsCount > 1) {
            positionTop = 51;
            // positionLeft = 193;
            // positionLeft = 148;
            positionLeft = 136;
        } else if (isElementsFocused && elementsCount == 1) {
            const elementKey = Object.keys(focusedElements)[0];
            const element = focusedElements[elementKey];

            if (element.type === 'table') {
                positionTop = 51;
                positionLeft = -10;
            } else if (element.type === 'note') {
                positionTop = 51;
                positionLeft = 283;
            } else if (element.type === 'pen-segment') {
                positionTop = 51;
                positionLeft = -9;
            }
        } else if (focusedLine) {
            positionTop = 51;
            // positionLeft = 245;
            // positionLeft = 354;
            positionLeft = 334;
        } else {
            positionTop = 51;
            // positionLeft = 193;
            positionLeft = 90;
        }

        return (
            <div
                className={`menu`}
                style={{
                    top: `${positionTop}px`,
                    position: 'absolute',
                    // left: `${positionLeft}px`,
                    left: subMenuLeftPx(positionLeft, -46),
                }}
            >
                <div className="controls-group color-picker">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.none == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, null);
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, null);
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], null);
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon
                                        isItem={true}
                                        color={
                                            isElementsFocused
                                                ? 'grey'
                                                : focusedLine
                                                    ? '#c8d9e0'
                                                    : 'grey'
                                        }
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.default')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.blue == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, 'blue');
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, 'blue');
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], 'blue');
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon
                                        isItem={true}
                                        color={'blue'}
                                    />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.blue')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.green == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, 'green');
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, 'green');
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], 'green');
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon isItem={true} color={'green'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.green')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.red == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, 'red');
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, 'red');
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], 'red');
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon isItem={true} color={'red'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.red')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.purple == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, 'purple');
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, 'purple');
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], 'purple');
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon isItem={true} color={'purple'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.purple')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${colorCounter.violet == itemsCount ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isElementsFocused) {
                                            setElementColor(getDiagramDoc(), focusedElementsByIdNoPens, 'violet');
                                        } else if (focusedLine) {
                                            setLineColor(getDiagramDoc(), focusedLine?.id, 'violet');
                                        } else {
                                            setAreaColor(getDiagramDoc(), focusedAreas[0], 'violet');
                                        }
                                    }}
                                >
                                    <BackgroundColorCircleIcon isItem={true} color={'violet'} />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="top"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.base.colors.violet')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // delete element menu
    const getDeleteElementMenu = (focusedElements, focusedGroupElements, focusedGroupAreas) => {
        const elementsCount = Object.keys(focusedElements).length;
        const groupElementsCount = Object.keys(focusedGroupElements).length;
        const groupAreasCount = Object.keys(focusedGroupAreas).length;
        if (!elementsCount && !groupElementsCount && !groupAreasCount) {
            return;
        }

        const allPens =
            elementsCount > 0 &&
            Object.values(focusedElements).every((el) => el?.type === 'pen-segment');

        let positionLeft = 0;

        if (allPens) {
            // Pen menu has fewer buttons than multi (no border/duplicate), so the
            // delete button sits further left.
            positionLeft = 201;
        } else if (elementsCount > 1) {
            positionLeft = 285;
        } else if (groupElementsCount || groupAreasCount) {
            // Group menu has fewer buttons than multi (no group/color/border), so the
            // delete button — and this confirmation under it — sits further left.
            // positionLeft = 185;
            positionLeft = 159;
            positionLeft = 184;
        } else {
            // positionLeft = 309;
            positionLeft = 209;
        }

        return (
            <div
                className={`menu`}
                style={{
                    top: '51px',
                    position: 'absolute',
                    left: subMenuLeftPx(positionLeft, -28),
                }}
            >
                <div className="controls-group">

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();

                                        if (groupElementsCount || groupAreasCount) {
                                            const doc = getDiagramDoc();
                                            deleteElements(doc, focusedElements);
                                            for (const areaId of focusedAreas) {
                                                deleteArea(doc, areaId);
                                            }
                                        } else {
                                            deleteElements(getDiagramDoc(), focusedElements || []);
                                        }
                                        setViewMode(null);
                                    }}
                                >
                                    <MarkYesIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.yes')}</span>
                                <span className="tooltip-shortcut">Enter</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider"></div>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        handleSubMenuClick();
                                    }}
                                >
                                    <MarkNoIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('b.no')}</span>
                                <span className="tooltip-shortcut">Esc</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>



                </div>
            </div>
        );
    };

    // note alignment menu
    const getNoteAlignmentMenu = (focusedElements) => {
        const keys = Object.keys(focusedElements);
        if (!keys?.length) {
            return;
        }
        const element = focusedElements[keys[0]];

        return (
            <div
                className={`menu alignment-menu`}
                // style={{ top: '51px', position: 'absolute', left: '133px' }}
                // style={{ top: '51px', position: 'absolute', left: '26px' }}
                style={{ top: '51px', position: 'absolute', left: '27px' }}
            >
                <div className="controls-group">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${(!element.alignmentHorizontal || element.alignmentHorizontal === 'left') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();

                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { horizontal: 'left' });
                                        }
                                    }}
                                >
                                    <AlignLeftIconNew />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.align_left')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${element.alignmentHorizontal === 'center' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { horizontal: 'center' });
                                        }
                                    }}
                                >
                                    <AlignCenterIconNew />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.align_center')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${element.alignmentHorizontal === 'right' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { horizontal: 'right' });
                                        }
                                    }}
                                >
                                    <AlignRightIconNew />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.align_right')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>

                {/* <div className="control-divider"></div> */}

                {/* <div className="controls-group">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${(!element.alignmentVertical || element.alignmentVertical === 'top') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { vertical: 'top' });
                                        }
                                    }}
                                >
                                    <AlignTopIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Top</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${element.alignmentVertical === 'center' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { vertical: 'center' });
                                        }
                                    }}
                                >
                                    <AlignVerticalCenterIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Center</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${element.alignmentVertical === 'bottom' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (element) {
                                            setNoteAlignment(getDiagramDoc(), element.id, { vertical: 'bottom' });
                                        }
                                    }}
                                >
                                    <AlignBottomIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Bottom</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div> */}

            </div>
        );
    };

    // note font styles menu
    const getNoteFontStylesMenu = (focusedElements) => {
        const keys = Object.keys(focusedElements);
        if (!keys?.length) {
            return;
        }
        const element = focusedElements[keys[0]];

        // While the note's editor overlay is open, the toggle buttons reflect the live
        // selection's marks; otherwise they fall back to the whole-text HTML (same
        // all-or-nothing semantics toggleStyleAll itself applies).
        const editor = getNoteEditor(element.id);
        const selectionStyles = editor ? editor.getSelectionStyles() : null;
        const isStyleActive = (style) => (
            selectionStyles ? !!selectionStyles[style] : isStyleOnAll(element.text ?? '', style)
        );

        return (
            <div
                className={`menu font-styles-menu`}
                // style={{ top: '51px', position: 'absolute', left: '32px' }}
                style={{ top: '51px', position: 'absolute', left: '-26px' }}
            // style={{ top: '51px', position: 'absolute', left: '-7px' }}
            >
                <div className="controls-group">
                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isStyleActive('bold') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        applyNoteStyle(element, 'bold');
                                    }}
                                >
                                    <BoldTextIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.bold')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isStyleActive('italic') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        applyNoteStyle(element, 'italic');
                                    }}
                                >
                                    <ItalicTextIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.italic')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isStyleActive('underline') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        applyNoteStyle(element, 'underline');
                                    }}
                                >
                                    <UnderlineTextIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.underline')}</span>

                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root delayDuration={600} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button ${isStyleActive('strike') ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        applyNoteStyle(element, 'strike');
                                    }}
                                >
                                    <StrikethroughTextIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctx.note.strikethrough')}</span>
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
        );
    };

    // on update / on delete trigger menu
    const getOnTriggerMenu = (focusedLine, triggerType = 'update') => {
        if (!focusedLine) {
            return;
        }

        const isOnUpdateTrigger = triggerType == 'update';
        const { onUpdateTrigger, onDeleteTrigger } = focusedLine;
        const trigger = isOnUpdateTrigger ? onUpdateTrigger : onDeleteTrigger;

        const isMoreOptionSelected = ['no_action', 'set_default'].includes(trigger);

        const positionLeft = isOnUpdateTrigger ? -23 : 75;


        return (
            <div
                className={`menu on-trigger-menu`}
                style={{ top: '51px', position: 'absolute', left: `${133 - 53 + positionLeft - 5}px` }}
            >
                <div className="controls-group">

                    <div className="control">
                        <button
                            className={`control-button`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                if (!canEdit) return;
                                handleSubMenuClick();
                                if (isMoreBtnClicked) {
                                    setIsMoreBtnClicked(false);
                                }
                                if (trigger) {
                                    setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: null });
                                }
                            }}
                            style={{ fontSize: '24px', fontWeight: 700 }}
                        >
                            <CircleNoneIcon />
                        </button>
                    </div>

                    <div className="control-divider"></div>

                    <div className="control">
                        <button
                            className={`control-button on-trigger-item ${trigger == 'cascade' ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                if (!canEdit) return;
                                handleSubMenuClick();
                                if (isMoreBtnClicked) {
                                    setIsMoreBtnClicked(false);
                                }
                                if (trigger !== 'cascade') {
                                    setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: 'cascade' });
                                }
                            }}
                        >
                            CASCADE
                        </button>
                    </div>

                    <div className="control-divider"></div>

                    <div className="control">
                        <button
                            className={`control-button on-trigger-item ${trigger == 'set_null' ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                if (!canEdit) return;
                                handleSubMenuClick();
                                if (isMoreBtnClicked) {
                                    setIsMoreBtnClicked(false);
                                }
                                if (trigger !== 'set_null') {
                                    setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: 'set_null' });
                                }
                            }}
                        >
                            SET NULL
                        </button>
                    </div>

                    <div className="control-divider"></div>

                    <div className="control">
                        <button
                            className={`control-button on-trigger-item ${trigger == 'restrict' ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                if (!canEdit) return;
                                handleSubMenuClick();
                                if (isMoreBtnClicked) {
                                    setIsMoreBtnClicked(false);
                                }
                                if (trigger !== 'restrict') {
                                    setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: 'restrict' });
                                }
                            }}
                        >
                            RESTRICT
                        </button>
                    </div>

                    <div className="control-divider"></div>

                    {/* closed more functions */}
                    {
                        !isMoreBtnClicked && !isMoreOptionSelected &&
                        <Fragment>
                            <div className="control">
                                <button
                                    className={`control-button more-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        setIsMoreBtnClicked(true);
                                    }}
                                >...</button>
                            </div>
                        </Fragment>
                    }


                    {/* open more functions */}
                    {
                        (isMoreBtnClicked || isMoreOptionSelected) &&
                        <Fragment>

                            <div className="control">
                                <button
                                    className={`control-button on-trigger-item ${trigger == 'no_action' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isMoreBtnClicked) {
                                            setIsMoreBtnClicked(false);
                                        }
                                        if (trigger !== 'no_action') {
                                            setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: 'no_action' });
                                        }
                                    }}
                                >
                                    NO ACTION
                                </button>
                            </div>

                            <div className="control-divider"></div>

                            <div className="control">
                                <button
                                    className={`control-button on-trigger-item set-default ${trigger == 'set_default' ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        handleSubMenuClick();
                                        if (isMoreBtnClicked) {
                                            setIsMoreBtnClicked(false);
                                        }
                                        if (trigger !== 'set_default') {
                                            setLineOnTrigger(getDiagramDoc(), focusedLine.id, { triggerType, value: 'set_default' });
                                        }
                                    }}
                                >
                                    SET DEFAULT
                                </button>
                            </div>

                        </Fragment>
                    }

                </div>
            </div >
        )
    };

    // handle outside click logic
    const handleMenuClick = (item = null) => {
        const descriptionItems = ['area-description', 'line-description', 'table-description', 'column-description', 'note-description', 'pen-description'];

        if (item == clickedItem) {
            if (descriptionItems.includes(clickedItem)) {
                dispatch(disableElementDescriptionEditing())
            }

            setClickedItem(null);
        } else {
            if (descriptionItems.includes(clickedItem)) {
                dispatch(disableElementDescriptionEditing())
            }
            if (descriptionItems.includes(item)) {
                dispatch(enableElementDescriptionEditing())
            }

            setClickedItem(item);
        }
    };
    const handleSubMenuClick = () => {
        setClickedItem(null);
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            const insideMenu = menuRef.current && menuRef.current.contains(event.target);
            // NoteLinkMenu's popover is portaled to document.body (see its
            // own doc comment) — not a DOM descendant of menuRef.current
            // even while it's open, so it needs its own explicit check here
            // or clicking inside it (the URL input, Apply) would read as
            // "outside" and close the whole menu out from under the user.
            const insideNoteLinkPopover = noteLinkPopoverRef.current && noteLinkPopoverRef.current.contains(event.target);
            // EditableNote's OWN in-editor link click popover (spec 8b,
            // task B) is ALSO portaled straight to document.body
            // (NoteLinkPreview.jsx, `.note-link-preview`) and has no ref
            // here to check `.contains()` against (it's owned/mounted by a
            // different component entirely) — a `closest()` lookup is the
            // only option, and is fine here since this only runs on the
            // rare `mousedown` event, not a hot path. Without this, a
            // mousedown on that popover's Edit/Remove buttons (or the URL
            // link) would misread as "outside THIS menu" and close whatever
            // ContextMenu submenu happens to be open, even though the two
            // popovers are unrelated.
            const insideEditorLinkClickPopover = !!(event.target && event.target.closest && event.target.closest('.note-link-preview'));
            if (!insideMenu && !insideNoteLinkPopover && !insideEditorLinkClickPopover) {
                setClickedItem(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // noteLinkEditRequestHandlerRef's own delegator (see its declaration for
    // why this needs to be a ref, not a bare effect closure): re-assigned on
    // every render so it always sees the CURRENT handleMenuClick (closes
    // over this render's `clickedItem`). Runs the EXACT SAME
    // noteLinkMenuArgsRef-then-handleMenuClick('note-link') sequence the
    // toolbar Link button's own onClick uses (search this file for
    // "beginLinkTarget (next line)") — just with canLink/prefillHref/
    // anchorRect supplied by the requester (EditableNote.jsx already ran
    // its OWN capture, over the clicked <a>'s full extent) instead of
    // computed here from a live selection.
    noteLinkEditRequestHandlerRef.current = ({ noteId, canLink, prefillHref, anchorRect }) => {
        const editor = getNoteEditor(noteId);
        noteLinkMenuArgsRef.current = { editor, canLink, prefillHref, anchorRect };
        handleMenuClick('note-link');
    };
    // Subscribes ONCE via a stable wrapper closure (always delegates to
    // whatever noteLinkEditRequestHandlerRef.current currently holds) — the
    // channel has exactly one listener slot; this component is the only
    // possible subscriber, and the ref above is what stays fresh across
    // renders, not this subscription itself. Symmetric subscribe/
    // unsubscribe, so React StrictMode's dev-only mount -> cleanup -> mount
    // self-heals (see noteLinkEditRequestBridge.js's own doc comment) —
    // unlike NoteLinkMenu's own effect, there is no painted DOM state here
    // for a phantom cleanup to lose, so no repaint-on-mount trick is needed.
    useEffect(() => subscribeNoteLinkEditRequest((payload) => noteLinkEditRequestHandlerRef.current(payload)), []);

    // handle mouse moves for hover/opacity for menu box
    // useEffect(() => {
    //     const handleMouseMove = (event) => {
    //         if (!menuRef.current) {
    //             return;
    //         }

    //         const HOVER_DISTANCE = 100;
    //         const mouseX = event.clientX;
    //         const mouseY = event.clientY;

    //         const menu = menuRef.current.getBoundingClientRect();
    //         const minX = menu.left - HOVER_DISTANCE;
    //         const maxX = menu.left + menu.width + HOVER_DISTANCE;
    //         const minY = menu.top - HOVER_DISTANCE;
    //         const maxY = menu.top + menu.height + HOVER_DISTANCE;

    //         if (
    //             mouseX >= minX &&
    //             mouseX <= maxX &&
    //             mouseY >= minY &&
    //             mouseY <= maxY
    //         ) {
    //             setIsHovered(true);
    //         } else {
    //             setIsHovered(false);
    //         }
    //     };

    //     document.addEventListener('mousemove', handleMouseMove);

    //     return () => {
    //         document.removeEventListener('mousemove', handleMouseMove);
    //     };
    // }, []);

    // handle change focusedIds → change view mode (table, column, line, area, group, etc.)
    function checkMenuState() {
        const lineCount = focusedLines.length;
        const areaCount = focusedAreas.length;
        const elementCount = focusedElements.length;

        if (lineCount === 1 && elementCount === 0 && areaCount === 0) {
            setViewMode('line');
            return;
        }
        if (areaCount === 1 && elementCount === 0 && lineCount === 0) {
            setViewMode('area');
            return;
        }
        if (elementCount === 0 && areaCount === 0 && lineCount === 0) {
            if (
                Object.keys(focusedGroupElements ?? {}).length ||
                Object.keys(focusedGroupAreas ?? {}).length
            ) {
                setViewMode('group');
            } else {
                setViewMode(null);
            }
            return;
        }
        if (elementCount === 1 && lineCount === 0 && areaCount === 0) {
            const elId = focusedElements[0];
            const el = elements[elId];
            if (el?.type === 'table') {
                if (
                    focusedInsideElementRef.current?.elementId &&
                    focusedInsideElementRef.current?.insideElementId
                ) {
                    if (viewMode !== 'column' && isMoreBtnClicked) {
                        setIsMoreBtnClicked(false);
                    }
                    setViewMode('column');
                } else {
                    const hadDifferentElement =
                        !focusedElementsRef.current ||
                        Object.keys(focusedElementsRef.current).length !== 1 ||
                        !focusedElementsRef.current[elId];
                    if (hadDifferentElement) setDragMenuDxDy({ dx: 0, dy: 0 });
                    setViewMode('table');
                }
            } else if (el?.type === 'note') {
                setViewMode('note');
            } else if (el?.type === 'pen-segment') {
                setViewMode('pen-segment');
            }
            return;
        }
        if (areaCount + elementCount > 1) {
            setViewMode(areaCount > 0 ? 'group' : 'multi-elements');
            return;
        }
        setViewMode(null);
    }

    useLayoutEffect(() => {
        setFocusedInsideElement(null);
        setClickedItem(null);
        checkMenuState();
        focusedElementsRef.current = Object.fromEntries(
            focusedElements.map((id) => [id, elements[id]])
        );
    }, [focusedIds]);

    useLayoutEffect(() => {
        focusedInsideElementRef.current = focusedInsideElement;
        setFocusedInsideElement(focusedInsideElement);
        checkMenuState();
        return () => {
            dispatch(disableElementDescriptionEditing());
        };
    }, [focusedInsideElement]);


    useLayoutEffect(() => {
        if (isFocusedInsideElementEmpty && viewMode == 'column') {
            setViewMode('table');
        }
    }, [isFocusedInsideElementEmpty]);


    useLayoutEffect(() => {
        // 1. update focused inside element

        // 2. check menu state
        // checkMenuState();
    }, [viewMode]);


    useEffect(() => {
        const handleDragMenuMouseDown = (event) => {
            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                return;
            }

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            setIsBtnDragMenuPressed(true);
            dragMenuStartXYRef.current = { x: event.clientX, y: event.clientY };
        };

        const handleDocumentMouseMove = (event) => {
            if (isBtnDragMenuPressed) {
                const mouseX = event.clientX;
                const mouseY = event.clientY;

                const dx = mouseX - dragMenuStartXYRef.current.x;
                const dy = mouseY - dragMenuStartXYRef.current.y;

                // dragMenuDxDyRef.current = {dx, dy};
                setDragMenuDxDy({
                    dx: dragMenuDxDy.dx + dx,
                    dy: dragMenuDxDy.dy + dy,
                });

                // count dx, dy
                // move menu.x & menu.y to dx, dy
                // coordinates = {x, y}
            }

            // if (isBtnDragMenuPressed && (Math.abs(mouseX - dragMenuStartXYRef.current.x) >= 20 || Math.abs(mouseY - dragMenuStartXYRef.current.y) >= 20)) {
            // 	dispatch(enableBtnNewTableGrab());
            // 	setIsBtnDragMenuPressed(false);
            // }
        };

        const handleDocumentClick = (event) => {
            // end of possible press, check first for no additional actions
            if (isBtnDragMenuPressed) {
                const mouseX = event.clientX;
                const mouseY = event.clientY;

                const dx = mouseX - dragMenuStartXYRef.current.x;
                const dy = mouseY - dragMenuStartXYRef.current.y;

                // click on the same place
                if (dx == 0 && dy == 0) {
                    setDragMenuDxDy({ dx: 0, dy: 0 });
                }


                setIsBtnDragMenuPressed(false);
            }
        };

        if (dragMenuRef.current) {
            dragMenuRef.current.addEventListener(
                'mousedown',
                handleDragMenuMouseDown
            );
            document.addEventListener('mousemove', handleDocumentMouseMove);
            document.addEventListener('click', handleDocumentClick);
        }

        return () => {
            if (dragMenuRef.current) {
                dragMenuRef.current.removeEventListener(
                    'mousedown',
                    handleDragMenuMouseDown
                );
                document.removeEventListener(
                    'mousemove',
                    handleDocumentMouseMove
                );
                document.removeEventListener('click', handleDocumentClick);
            }
        };
    }, [dispatch, isBtnDragMenuPressed, viewMode]);

    useEffect(() => {
        const handleKeyPress = (event) => {
            const isCtrl = isMac ? event.metaKey : event.ctrlKey;

            // const isOpenTextEditing = focusedInsideElementRedux || isEnabledElementDescriptionEditing;
            const isOpenTextEditing = focusedInsideElement && focusedInsideElement?.insideElementType === 'column';
            const isOpenHeaderEditing = focusedInsideElement && focusedInsideElement?.insideElementType === 'header';
            const isOpenNoteEditing = focusedInsideElement && focusedInsideElement?.insideElementType === 'note-text';

            const isTextEditing =
                // isOpenTextEditing ||
                isModalDiagramDetailsEnabled ||
                isModalSupportEnabled ||
                isModalDiagramShareEnabled ||
                isBtnSearchEnabled;

            // Ignore events from disabled or read-only elements
            if (event.target.disabled || event.target.readOnly || isTextEditing)
                return;

            if (['note-description', 'pen-description', 'table-description', 'column-description', 'area-description', 'line-description'].includes(clickedItem)) {
                // -----------------------------------------------------------------------------
                // + [TAB] = [MOVE FOCUS ON INPUT/BUTTON]
                if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();

                    if (document.activeElement === descriptionMenuInputRef.current) {
                        descriptionMenuButtonRef.current.focus();
                    } else {
                        descriptionMenuInputRef.current.focus();
                    }
                }
            } else if (clickedItem === 'enum') {
                // -----------------------------------------------------------------------------
                // + [TAB] = [MOVE FOCUS ON INPUT/BUTTON]
                if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();

                    if (document.activeElement === enumValueMenuInputRef.current) {
                        enumValueMenuButtonRef.current.focus();
                    } else {
                        enumValueMenuInputRef.current.focus();
                    }
                }
            } else if (clickedItem === 'default-value') {
                // -----------------------------------------------------------------------------
                // + [TAB] = [MOVE FOCUS ON INPUT/BUTTON]
                if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();

                    // if (document.activeElement === defaultValueMenuInputRef.current) {
                    //     defaultValueMenuButtonRef.current.focus();
                    // } else if (document.activeElement === defaultValueMenuButtonRef.current) {
                    //     defaultValueMenuInputRef.current.focus();
                    // } else {
                    //     defaultValueMenuInputRef.current.focus();
                    // }
                    if (document.activeElement === defaultValueMenuInputRef.current) {
                        defaultValueMenuButtonRef.current.focus();
                    } else {
                        defaultValueMenuInputRef.current.focus();
                    }
                }
            } else if (clickedItem === 'generated-value') {
                // -----------------------------------------------------------------------------
                // + [TAB] = [MOVE FOCUS ON INPUT/BUTTON]
                if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();

                    // if (document.activeElement === generatedValueMenuInputRef.current) {
                    //     generatedValueMenuButtonRef.current.focus();
                    // } else {
                    //     if (generatedValueMenuInputRef.current) {
                    //         generatedValueMenuInputRef.current.focus();
                    //     }
                    // }

                    if (document.activeElement === generatedValueMenuInputRef.current) {
                        generatedValueMenuButtonRef.current.focus();
                    } else if (document.activeElement === generatedValueMenuButtonRef.current) {
                        generatedValueMenuInputRef.current.focus();
                        // } else {
                        //     generatedValueMenuInputRef.current.focus();
                    }

                }
            } else if (clickedItem === 'check-value') {
                // -----------------------------------------------------------------------------
                // + [TAB] = [MOVE FOCUS ON INPUT/BUTTON]
                if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();

                    if (document.activeElement === checkValueMenuInputRef.current) {
                        checkValueMenuButtonRef.current.focus();
                    } else {
                        checkValueMenuInputRef.current.focus();
                    }
                }
            } else if (isOpenTextEditing || isOpenNoteEditing) {
                if (!canEdit) return;
                // + [CTRL + 1]
                if (
                    event.code === 'Digit1' && isCtrl &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    handleSubMenuClick();

                    if (
                        focusedInsideElementRef.current?.elementId &&
                        focusedInsideElementRef.current?.insideElementId
                    ) {
                        const isPK =
                            elements[focusedInsideElementRef.current?.elementId]
                                ?.columns?.[focusedInsideElementRef.current?.insideElementId]?.isPK;

                        const newValue = isPK ? null : 'pk';
                        setColumnKey(
                            getDiagramDoc(),
                            focusedInsideElementRef.current?.elementId,
                            focusedInsideElementRef.current?.insideElementId,
                            newValue
                        );
                    }
                }
                // + [CTRL + 2]
                else if (
                    event.code === 'Digit2' && isCtrl &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSubMenuClick();

                    if (
                        focusedInsideElementRef.current?.elementId &&
                        focusedInsideElementRef.current?.insideElementId
                    ) {
                        const isFK =
                            elements[focusedInsideElementRef.current?.elementId]
                                ?.columns?.[focusedInsideElementRef.current?.insideElementId]?.isFK;

                        const newValue = isFK ? null : 'fk';
                        setColumnKey(
                            getDiagramDoc(),
                            focusedInsideElementRef.current?.elementId,
                            focusedInsideElementRef.current?.insideElementId,
                            newValue
                        );
                    }
                }
                // // + [CTRL + 3]
                // else if (
                //     event.code === 'Digit3' && isCtrl &&
                //     !event.shiftKey &&
                //     !event.altKey
                // ) {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     handleSubMenuClick();

                //     if (
                //         focusedInsideElementRef.current?.elementId &&
                //         focusedInsideElementRef.current?.insideElementId
                //     ) {
                //         dispatch(
                //             setColumnNN(
                //                 focusedInsideElementRef.current?.elementId,
                //                 focusedInsideElementRef.current?.insideElementId
                //             )
                //         );
                //     }
                // }
                // // + [CTRL + 4]
                // else if (
                //     event.code === 'Digit4' && isCtrl &&
                //     !event.shiftKey &&
                //     !event.altKey
                // ) {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     handleSubMenuClick();

                //     if (
                //         focusedInsideElementRef.current?.elementId &&
                //         focusedInsideElementRef.current?.insideElementId
                //     ) {
                //         const indexType =
                //             focusedInsideElementRef.current?.insideElement
                //                 ?.index?.type;

                //         const newValue =
                //             indexType === 'regular' ? null : 'regular';
                //         dispatch(
                //             setColumnIndex(
                //                 focusedInsideElementRef.current?.elementId,
                //                 focusedInsideElementRef.current
                //                     ?.insideElementId,
                //                 newValue
                //             )
                //         );
                //     }
                // }
                // // + [CTRL + 5]
                // else if (
                //     event.code === 'Digit5' && isCtrl &&
                //     !event.shiftKey &&
                //     !event.altKey
                // ) {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     handleSubMenuClick();

                //     if (
                //         focusedInsideElementRef.current?.elementId &&
                //         focusedInsideElementRef.current?.insideElementId
                //     ) {
                //         const indexType =
                //             focusedInsideElementRef.current?.insideElement
                //                 ?.index?.type;

                //         const newValue =
                //             indexType === 'unique' ? null : 'unique';
                //         dispatch(
                //             setColumnIndex(
                //                 focusedInsideElementRef.current?.elementId,
                //                 focusedInsideElementRef.current
                //                     ?.insideElementId,
                //                 newValue
                //             )
                //         );
                //     }
                // }
                // // + [CTRL + 6]
                // else if (
                //     event.code === 'Digit6' && isCtrl &&
                //     !event.shiftKey &&
                //     !event.altKey
                // ) {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     handleSubMenuClick();

                //     if (
                //         focusedInsideElementRef.current?.elementId &&
                //         focusedInsideElementRef.current?.insideElementId
                //     ) {
                //         const indexType =
                //             focusedInsideElementRef.current?.insideElement
                //                 ?.index?.type;

                //         const newValue =
                //             indexType === 'composite' ? null : 'composite';
                //         dispatch(
                //             setColumnIndex(
                //                 focusedInsideElementRef.current?.elementId,
                //                 focusedInsideElementRef.current
                //                     ?.insideElementId,
                //                 newValue
                //             )
                //         );
                //     }
                // }
                // // + [CTRL + 7]
                // else if (
                //     event.code === 'Digit7' && isCtrl &&
                //     !event.shiftKey &&
                //     !event.altKey
                // ) {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     handleSubMenuClick();

                //     if (
                //         focusedInsideElementRef.current?.elementId &&
                //         focusedInsideElementRef.current?.insideElementId
                //     ) {
                //         const indexType =
                //             focusedInsideElementRef.current?.insideElement
                //                 ?.index?.type;

                //         const newValue =
                //             indexType === 'fullText' ? null : 'fullText';
                //         dispatch(
                //             setColumnIndex(
                //                 focusedInsideElementRef.current?.elementId,
                //                 focusedInsideElementRef.current
                //                     ?.insideElementId,
                //                 newValue
                //             )
                //         );
                //     }
                // }
                // // + [ESC]
                // else if (event.code === 'Escape') {
                //     event.preventDefault();
                //     event.stopPropagation();
                //     // handleMenuClick();

                //     if (clickedItem) {
                //         handleSubMenuClick();
                //     } else {
                //         dispatch(clickEscape());
                //     }
                //     console.log('Context Menu NEW Escape', clickedItem)
                // }

            } else {
                if (!canEdit) return;
                // -----------------------------------------------------------------------------
                // +/- [DELETE] = [OPEN DELETE CONTEXT MENU / OR DELETE ELEMENT]
                if (
                    event.code === 'Delete' &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    if (isOpenHeaderEditing || isOpenNoteEditing) {
                        // event.preventDefault();
                        // nothing
                    } else if (['table'].includes(viewMode)) {
                        handleMenuClick('delete-element');
                    } else if (['multi-elements'].includes(viewMode)) {
                        const isFocusedElementsHasTable = Object.values(focusedElements).some((element) => element.type === 'table');
                        if (isFocusedElementsHasTable) {
                            handleMenuClick('delete-element');
                        } else {
                            handleMenuClick();
                            deleteElements(getDiagramDoc(), focusedElements);
                            setViewMode(null);
                        }
                    } else if (
                        viewMode === 'note' ||
                        viewMode === 'pen-segment'
                    ) {
                        handleMenuClick();
                        deleteElements(getDiagramDoc(), focusedElements);
                        setViewMode(null);
                    } else if (viewMode === 'area') {
                        handleMenuClick();
                        deleteArea(getDiagramDoc(), focusedAreas[0]);
                        setViewMode(null);
                    } else if (viewMode === 'line') {
                        handleMenuClick();
                        deleteLine(getDiagramDoc(), focusedLines[0]);
                        setViewMode(null);
                    } else if (viewMode === 'group') {
                        // handleMenuClick();
                        // dispatch(deleteFocusedGroup());
                        handleMenuClick('delete-element');
                    }
                }
                // -----------------------------------------------------------------------------
                // +/- [CTRL + DELETE] = [NOW => DELETE ELEMENT] [SHOULD BE => OPEN DELETE CONTEXT MENU]
                else if (
                    event.code === 'Delete' && isCtrl &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    event.preventDefault();
                    if (isOpenHeaderEditing || isOpenNoteEditing) {
                        handleMenuClick('delete-element');
                    }
                }
                // + [ESC]
                else if (event.code === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    // handleMenuClick();
                    handleSubMenuClick();
                    // console.log('Context Menu Escape', clickedItem)
                }
                // + [ENTER] = [DELETE ELEMENT WHEN OPEN DELETE-MENU]
                // [WHEN HEADER EDITING] [FOCUS ON FIRST COLUMN / ADD NEW FIRST COLUMN]
                else if (
                    (event.code === 'Enter' || event.code === 'NumpadEnter') &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    event.preventDefault();

                    if (isOpenHeaderEditing || isOpenNoteEditing) {
                        if (clickedItem === 'delete-element') {
                            handleSubMenuClick();
                            deleteElements(getDiagramDoc(), focusedElements);
                            setViewMode(null);
                        } else if (!clickedItem) {
                            const tableId = focusedInsideElement.elementId;
                            const colOrder = elements[tableId]?.columnsOrder;
                            if (colOrder?.length) {
                                const firstColId = colOrder[0];
                                dispatch(updateFocusedInsideElement(tableId, firstColId, 0, 'column'));
                            } else {
                                const newColId = nanoid();
                                addColumn(getDiagramDoc(), { tableId, mode: 'first', column: { id: newColId, name: 'column_new', type: 'int' } });
                                dispatch(updateFocusedInsideElement(tableId, newColId, 0, 'column'));
                            }
                        }
                    } else if (viewMode === 'group') {
                        handleSubMenuClick();
                        const doc = getDiagramDoc();
                        deleteElements(doc, focusedElements);
                        for (const areaId of focusedAreas) {
                            deleteArea(doc, areaId);
                        }
                        setViewMode(null);
                    } else {
                        if (clickedItem === 'delete-element') {
                            handleSubMenuClick();
                            deleteElements(getDiagramDoc(), focusedElements);
                            setViewMode(null);
                        }
                    }
                }
                // + [CTRL + ENTER] = [ADD NEW FIRST COLUMN WHEN HEADER EDITING]
                else if (
                    (event.code === 'Enter' || event.code === 'NumpadEnter') && isCtrl &&
                    !event.altKey
                ) {
                    event.preventDefault();
                    if ((isOpenHeaderEditing || isOpenNoteEditing) && !clickedItem) {
                        const tableId = focusedInsideElement.elementId;
                        const newColId = nanoid();
                        addColumn(getDiagramDoc(), { tableId, mode: 'first', column: { id: newColId, name: 'column_new', type: 'int' } });
                        dispatch(updateFocusedInsideElement(tableId, newColId, 0, 'column'));
                    }
                    // -----------------------------------------------------------------------------
                }

                // [ A ] = [CREATE AREA FOR SELECTED ELEMENTS]
                else if (
                    event.code === 'KeyA' &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    if (viewMode === 'multi-elements' && focusedElements?.length) {
                        event.preventDefault();
                        const areaId = nanoid();
                        addAreaForFocusedElements(getDiagramDoc(), {
                            focusedElementsIds: focusedElements,
                            areaProps: { id: areaId },
                        });
                        // Focus the new area (checkMenuState then switches the menu to
                        // the area view), matching the saga-driven area creation.
                        dispatch(updateFocusedIds([areaId]));
                    }
                }

                // +/- [CTRL + C] = [DUPLICATE ELEMENT]
                else if (
                    event.code === 'KeyC' && isCtrl &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    if (isOpenHeaderEditing || isOpenNoteEditing) {
                        // event.preventDefault();
                        // nothing
                    } else if (
                        [
                            'table',
                            'note',
                            'pen-segment',
                            'multi-elements',
                        ].includes(viewMode)
                    ) {
                        if (!canDuplicateTables(focusedElements) || !canDuplicateNotes(focusedElements)) return;
                        const newIds = duplicateElements(getDiagramDoc(), focusedElements);
                        if (newIds.length) {
                            dispatch(updateFocusedIds(newIds));
                            // Header focus (for inline rename) only when duplicating a
                            // single element — not a multi-selection.
                            if (newIds.length === 1) {
                                dispatch(updateFocusedInsideElement(newIds[0], null, null, 'header'));
                            }
                        }
                        setViewMode(null);
                    } else if (viewMode === 'group') {
                        const doc = getDiagramDoc();
                        if (!canDuplicateTables(focusedElements) || !canDuplicateNotes(focusedElements)) return;
                        const { newElementIds, newAreaIds } = duplicateGroup(doc, {
                            elementIds: focusedElements,
                            areaIds: focusedAreas,
                        });
                        const allNewIds = [...newElementIds, ...newAreaIds];
                        if (allNewIds.length) dispatch(updateFocusedIds(allNewIds));
                        setViewMode(null);
                    } else if (viewMode == 'area') {
                        duplicateArea(getDiagramDoc(), focusedAreas[0]);
                        setViewMode(null);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [
        focusedInsideElement,
        focusedElements,
        focusedAreas,
        isModalDiagramDetailsEnabled,
        isModalSupportEnabled,
        isModalDiagramShareEnabled,
        isBtnSearchEnabled,
        clickedItem,
        viewMode,
        canEdit,
    ]);



    useEffect(() => {
        setDragMenuDxDy({ dx: 0, dy: 0 });
        return () => { };
    }, [viewMode]);

    const isShowedMenu = isContextMenuShowed && boardViewMode === 'full' && !isNotElementInViewPort && !isBtnSearchEnabled;

    return (
        <div
            ref={menuRef}
            className={`context-menu ${isHovered || isContextMenuVisible ? 'mouse-hovered' : ''}`}
            style={positionMenuStyle}
        >
            {!isContextMenuShowed && viewMode && !isBtnSearchEnabled && wrappedMenu}

            {isShowedMenu && viewMode === 'table' && getTableMenu(focusedElementsById)}
            {isShowedMenu && viewMode === 'column' && getColumnMenu()}

            {isShowedMenu && viewMode === 'line' && getLineMenu(focusedLine)}
            {isShowedMenu && viewMode === 'area' && getAreaMenu(focusedArea)}


            {isShowedMenu && clickedItem === 'enum' && <EnumMenu />}

            {/* ------------------------------------------------------------ */}

            {isShowedMenu && viewMode === 'note' && getNoteMenu(focusedElementsById)}

            {isShowedMenu && viewMode === 'pen-segment' && getPenSegmentMenu(focusedElementsById)}

            {isShowedMenu && viewMode === 'multi-elements' && (isAllPensSelection
                ? getMultiPenSegmentsMenu(focusedElementsById)
                : getMultiElementsMenu(focusedElementsById))}
            {isShowedMenu && viewMode === 'group' && getGroupMenu(focusedGroupElements, focusedGroupAreas)}

            {/* {isShowedMenu && clickedItem === 'enum' && <EnumMenu />} */}
            {isShowedMenu && clickedItem === 'default-value' && <DefaultValueMenu />}

            {isShowedMenu && clickedItem === 'generated-value' && <GeneratedValueMenu />}
            {isShowedMenu && clickedItem === 'check-value' && <CheckValueMenu />}

            {isShowedMenu && clickedItem === 'note-description' && <DescriptionMenu type={'note'} />}
            {isShowedMenu && clickedItem === 'pen-description' && <DescriptionMenu type={'pen'} />}
            {isShowedMenu && clickedItem === 'table-description' && <DescriptionMenu type={'table'} />}

            {isShowedMenu && clickedItem === 'column-description' && <DescriptionMenu type={'column'} />}

            {isShowedMenu && clickedItem === 'area-description' && <DescriptionMenu type={'area'} />}
            {isShowedMenu && clickedItem === 'line-description' && <DescriptionMenu type={'line'} />}


            {isShowedMenu && clickedItem === 'on-update-trigger' && getOnTriggerMenu(focusedLine, 'update')}
            {isShowedMenu && clickedItem === 'on-delete-trigger' && getOnTriggerMenu(focusedLine, 'delete')}

            {isShowedMenu && clickedItem === 'key' && getKeyMenu()}
            {isShowedMenu && clickedItem === 'index' && getIndexMenu()}

            {isShowedMenu && clickedItem === 'left-line-connector' && getleftLineConnectorMenu(focusedLine)}
            {isShowedMenu && clickedItem === 'right-line-connector' && getRightLineConnectorMenu(focusedLine)}
            {isShowedMenu && clickedItem === 'line-solid-type' && getLineSolidTypeMenu(focusedLine)}

            {isShowedMenu && clickedItem === 'element-borders' && getElementBorderMenu(focusedElementsById, focusedArea)}
            {isShowedMenu && clickedItem === 'color-picker' && getColorPickerMenu(focusedElementsById, focusedLine, focusedArea)}
            {isShowedMenu && clickedItem === 'pen-color-picker' && getPenColorPickerMenu(focusedElementsById)}
            {isShowedMenu && clickedItem === 'filter-selection' && getFilterSelectionMenu(filterSelectionSet)}

            {isShowedMenu && clickedItem === 'align' && getNoteAlignmentMenu(focusedElementsById)}
            {isShowedMenu && clickedItem === 'font-styles' && getNoteFontStylesMenu(focusedElementsById)}
            {isShowedMenu && clickedItem === 'note-link' && (
                <NoteLinkMenu
                    {...noteLinkMenuArgsRef.current}
                    canEdit={canEdit}
                    t={t}
                    onSubMenuClose={handleSubMenuClick}
                    popoverRef={noteLinkPopoverRef}
                />
            )}

            {isShowedMenu && clickedItem === 'delete-element' && getDeleteElementMenu(focusedElementsById, focusedGroupElements, focusedGroupAreas)}
            {/* {isShowedMenu && clickedItem === 'delete-column' && deleteColumnMenu} */}
            {/* {isShowedMenu && clickedItem === 'delete-line' && deleteLineMenu} */}
        </div>
    );
};

export default ContextMenu;
